import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { Subject, BehaviorSubject } from 'rxjs';
import {
  takeUntil,
  filter,
  distinctUntilChanged,
  debounceTime,
  take,
  tap,
} from 'rxjs/operators';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { ComponentService } from '../../services/component/component.service';
import { ComponentInputBase } from '../../models/ComponentInputBase.model';
import { Store } from '@ngrx/store';
import * as DataActions from '../../ngrx/data/data.actions';
import { LoadDataParams } from '../../interfaces/DataSource/DataSourceParams.interface';
import { AppState } from '../../ngrx/template/template.reducers';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { ControlContainer, FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Router, NavigationEnd } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  getEventTypeLabelDa,
  getEventTypeMaterialIcon,
} from '../../utils/event-type-display.util';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { DunaValidationBadgeComponent } from '../duna/duna-validation-badge/duna-validation-badge.component';
import { DunaUserProgressLabelComponent } from '../duna/duna-user-progress-label/duna-user-progress-label.component';
import { DunaEducationalPathBadgeComponent } from '../duna/duna-educational-path-badge/duna-educational-path-badge.component';
import { RowDetailsModalComponent } from './row-details-modal/row-details-modal.component';
import { TemplateService } from '../../services/template/template.service';
import { ListRequestDTO } from '../../dto/list-request.dto';
import { ListResponseDTO } from '../../dto/list-response.dto';
import { ComponentDataSourceService } from '../../services/component-data-source/component-data-source.service';
import { environment } from '../../environment/environment';
import {
  FacetFilterConfig,
  FacetFilterExistingSelection,
  FacetOption,
  mapFacetsToTableFilters,
} from '../../interfaces/DataSource/FacetFilterConfig.interface';
import { parsePlatformFromDeviceInfo } from '../../services/component-data-source/data/cprng/dunaUsers';
import {
  getCachedDataUserUuid,
  isProfileUrlScopedDataSource,
} from '../../services/component-data-source/data/profile/profile-url-scoped-datasource.util';
import { UserService } from '../../services/user/user.service';

// Remove the conflicting global declaration and use a type definition instead
type SurveyJSInitFunction = (component: any, data: any) => void;

// New interface for SelectionInfo
interface SelectedRowInfo {
  row: any;
  identifier: string;
}

interface TableOption {
  id: string;
  label: string;
  type: 'toggle' | 'select' | 'button';
  default?: boolean;
  options?: Array<{ value: any; label: string }>;
  onChange?: (value: any) => void;
}

interface TableMenuItemDef {
  label: string;
  icon?: string;
  color?: string;
  showIf?: (row: any) => boolean;
  action: (row: any) => void;
}

interface TableColumn {
  key: string;
  header: string;
  type:
    | 'text'
    | 'button'
    | 'icon-button'
    | 'date'
    | 'boolean'
    | 'select'
    | 'component'
    | 'menu';
  action?: (row: any) => void;
  buttonText?: string;
  buttonColor?: string;
  buttonTextFn?: (row: any) => string;
  buttonColorFn?: (row: any) => string | undefined;
  variantFn?: (row: any) => string | undefined;
  buttonClassFn?: (row: any) => string | string[] | undefined;
  icon?: string;
  format?: (value: any) => string;
  transform?: (value: any, row?: any) => string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  /** Shrink the column to header/content width instead of sharing leftover space. */
  fitContent?: boolean;
  /** Give this column leftover table space (typically name-like text columns). */
  fill?: boolean;
  variant?: string;
  wrap?: boolean;
  alignment?: 'left' | 'center' | 'right';
  options?: Array<{ value: any; label: string }>;
  onChange?: (newValue: any, rowData: any) => void;
  editable?: boolean;
  editor?: 'text' | 'select' | 'date' | 'boolean';
  dynamicallyAdded?: boolean;
  showIf?: (row: any) => boolean;
  // Component column
  componentName?: string;
  componentInputs?: (row: any) => { [key: string]: any };
  // Menu column
  menuItems?: TableMenuItemDef[];
  // Cell interaction
  onClick?: (row: any) => void;
  tooltip?: (row: any) => string;
  subtitle?: (row: any) => string | null;
  /** Row field used with Material icon before cell text (e.g. event_type). */
  eventTypeIconFrom?: string;
  /** When true, the button opens the row details dialog instead of running `action`. */
  opensRowDetailsModal?: boolean;
  /** Full value formatter for the row details dialog (table may still use truncated `transform`). */
  detailTransform?: (value: any, row?: any) => string;
}

interface TableData {
  items: any[];
  totalCount?: number;
  pageSize?: number;
  pageIndex?: number;
}

interface TableFilter {
  column: string;
  value: any;
  operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan';
  label: string;
  options: Array<{
    label: string;
    value: any;
  }>;
}

interface TableSettings {
  columns: TableColumn[];
  pageable?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  sortable?: boolean;
  filterable?: boolean;
  rowClickable?: boolean;
  onRowClick?: (row: any) => void;
  rowHover?: boolean;
  striped?: boolean;
  dense?: boolean;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
  searchable?: boolean;
  searchPlaceholder?: string;
  focusSearchOnLoad?: boolean;
  filters?: TableFilter[];
  clientSideFiltering?: boolean;
  multiSelect?: boolean; // Enable/disable multiselect
  batchEditableColumns?: string[]; // List of column keys that can be batch edited
  tableOptions?: TableOption[]; // Options/controls to show at the top of the table
  layout?: 'auto' | 'table' | 'card';
  /** Column sizing: `fixed` shares width equally; `auto` lets columns use the width they need. */
  tableLayout?: 'fixed' | 'auto';
  // Multi-select remove functionality
  showRemoveButton?: boolean; // Show remove button when items are selected
  removeButtonText?: string; // Text for the remove button (default: "Slet valgte")
  removeButtonColor?: string; // Color for the remove button (default: "warn")
  onRemoveSelected?: (selectedItems: any[]) => void; // Callback when remove button is clicked
  /** Extra label/value pairs appended in the row details dialog. */
  rowDetailExtras?: Array<{ label: string; value: (row: any) => string }>;
}

@Component({
  selector: 'app-dynamic-table',
  standalone: true,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule,
    MatMenuModule,
    DunaValidationBadgeComponent,
    DunaEducationalPathBadgeComponent,
    DunaUserProgressLabelComponent,
  ],
  templateUrl: './dynamic-table.component.html',
  styleUrl: './dynamic-table.component.scss',
  viewProviders: [{ provide: ControlContainer, useValue: null }],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class DynamicTableComponent
  extends ComponentInputBase
  implements OnInit, OnDestroy
{
  /** Applied to mat-select overlay panels (rendered outside the table DOM). */
  readonly selectPanelClass = 'dynamic-table-select-panel';

  @Input() dataSource: TableData = {
    items: [],
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  };

  @Input() settings: TableSettings = {
    columns: [
      {
        key: 'message',
        header: 'Status',
        type: 'text',
        sortable: false,
        filterable: false,
        wrap: false,
      },
    ],
    pageable: false,
    sortable: false,
    filterable: false,
    rowHover: false,
    striped: false,
    dense: false,
    searchable: false,
    defaultSortColumn: '',
    defaultSortDirection: 'desc',
    multiSelect: false,
  };

  @Input() simpleMultiSelect: boolean = false;
  @Input() selectionKeyField: string = '';
  @Input() persistSelections: boolean = false;
  @Input() persistSearch: boolean = true;

  @Output() rowSelectionChange = new EventEmitter<any[]>();
  @Output() simpleSelectionChange = new EventEmitter<any[]>();

  displayedColumns: string[] = [];
  pageSize = 10;
  pageIndex = 0;
  paginatedData$ = new BehaviorSubject<any[]>([]);

  // Multiselect properties
  selection = new SelectionModel<any>(true, []);
  batchUpdateValues: { [key: string]: any } = {};
  isBatchUpdating = false;

  // Add storage for persistent selections across pagination/filtering
  private persistentSelections: Map<string, any> = new Map<string, any>();

  private destroy$ = new Subject<void>();
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  // Add ViewChild references to sort headers for better control
  @ViewChild('sortHeader', { static: false }) sortHeader: any;

  // Custom sort state tracking
  private currentSortColumn: string = '';
  private currentSortDirection: 'asc' | 'desc' | '' = '';
  private isCustomSortActive: boolean = false;

  noDataMessage = 'Ingen data tilgængelig';
  searchText = '';
  private searchSubject = new Subject<string>();
  private lastRouteBase: string | null = null;
  private lastFullUrl: string | null = null;

  private _internalFilters: TableFilter[] = [];

  /** UI-bound filters — decoupled from settings merges that may drop filters. */
  displayFilters: TableFilter[] = [];

  private focusSearchPollingId: ReturnType<typeof setInterval> | null = null;

  // Table options state
  tableOptionsState: { [key: string]: any } = {};

  isMobile = false;
  private isApplyingPagination = false; // Flag to prevent infinite loops
  private tableSettingsInitialized = false;
  private initialDataLoadRequested = false;

  private elementRef = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  public isSurveyJSComponent = false;

  private static readonly SEARCH_STORAGE_PREFIX = 'duna.dynamicTable.search.';

  @Input() useStandardizedApi: boolean = false;
  @Input() standardizedApiEndpoint: string = '';

  constructor(
    componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private router: Router,
    private dialog: MatDialog,
    private templateService: TemplateService,
    private dataSourceService: ComponentDataSourceService,
    private userService: UserService
  ) {
    super(componentService);

    // Ensure dynamicInput is always initialized
    this.dynamicInput = {
      dataSource: {
        type: 'text',
        value: '',
        displayText: 'Data Source Key',
        description:
          'Specify the data source identifier (e.g., "users", "events")',
      },
      enableSearch: {
        type: 'boolean',
        value: false,
        displayText: 'Enable Search Bar',
        description: 'Shows a search box above the table for text filtering',
      },
      enableMultiSelect: {
        type: 'boolean',
        value: false,
        displayText: '(old) Enable Multi-Select',
        description:
          'DEPRECATED: Use the "Enable Row Selection" setting in the survey editor instead',
      },
      simpleMultiSelect: {
        type: 'boolean',
        value: false,
        displayText: '(old) Simple Multi-Select Mode',
        description:
          'DEPRECATED: Use "Enable Row Selection" in the survey editor instead',
      },
      selectionKeyField: {
        type: 'text',
        value: '',
        displayText: 'Selection Key Field',
        description:
          'Column name for unique identifier when using row selection (e.g., "id", "userId")',
      },
      persistSelections: {
        type: 'boolean',
        value: false,
        displayText: 'Remember Selections Across Pages',
        description:
          'Keep row selections when user navigates between pages or applies filters',
      },
      persistSearch: {
        type: 'boolean',
        value: true,
        displayText: 'Remember Search Across Navigation',
        description:
          'Keep the search filter when navigating away and back (browser session only)',
      },
      filters: {
        type: 'json',
        value: '[]',
        displayText: '(old) Advanced Filters',
        description:
          'DEPRECATED: Complex filter configuration. Use the search bar for most cases',
      },
      useStandardizedApi: {
        type: 'boolean',
        value: false,
        displayText: 'Use Standardized API',
        description:
          'Enable if your data source follows the standardized API format',
      },
      standardizedApiEndpoint: {
        type: 'text',
        value: '',
        displayText: 'Standardized API Endpoint',
        description: 'Custom API endpoint when using standardized API mode',
      },
      enablePrint: {
        type: 'boolean',
        value: false,
        displayText: 'Enable Print Button',
        description:
          'Shows a printer icon that allows users to print the table content',
      },
      batchEditableColumns: {
        type: 'json',
        value: '[]',
        displayText: 'Batch Editable Columns',
        description:
          'JSON array of column keys that can be batch edited. Example: ["status", "role"]. Columns must exist in your data source and have editable: true configured.',
      },
      tableTitle: {
        type: 'text',
        value: '',
        displayText: 'Tabeloverskrift',
        description:
          'Valgfri overskrift der vises over tabellen (H2 størrelse). Efterlad tom for ingen overskrift.',
      },
      tableDescription: {
        type: 'textarea',
        value: '',
        displayText: 'Tabelbeskrivelse',
        description:
          'Valgfri beskrivelsestekst der vises under overskriften og over tabellen. Efterlad tom for ingen beskrivelse.',
      },
    };

    this.checkIfMobile();

    // Subscribe to search input changes
    this.searchSubject
      .pipe(
        debounceTime(500), // Increased debounce time for API calls
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Reset to first page when applying search
        this.pageIndex = 0;

        // Apply search filter
        this.loadDataWithFilters();

        // Ensure pagination is applied after data is loaded
        setTimeout(() => {
          this.applyPagination();
          this.cdr.detectChanges();
        }, 100);
      });

    // Subscribe to router events to detect route changes
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const currentUrl = event.urlAfterRedirects;

        // Extract the base route (e.g., '/lokation' from '/lokation/some-uuid')
        const currentRouteBase = this.getRouteBase(currentUrl);

        // Get the data source key
        const dataSourceKey = this.getInput('dataSource');
        if (!dataSourceKey) {
          // No data source key, nothing to reload
          return;
        }

        // Check if we're navigating to the same type of route
        const isSameRouteType =
          this.lastRouteBase && currentRouteBase === this.lastRouteBase;

        // Check if we're revisiting the exact same URL
        const isRevisitingSameUrl = currentUrl === this.lastFullUrl;

        // Log for debugging

        // Reload data if:
        // 1. We're navigating to the same type of route (e.g., from one location to another)
        // 2. OR we're revisiting the exact same URL (e.g., navigating away and then back)
        if (isSameRouteType || isRevisitingSameUrl) {
          this.reloadDataSource();
        }

        // Update the last route base and full URL
        this.lastRouteBase = currentRouteBase;
        this.lastFullUrl = currentUrl;
      });

    // Subscribe to viewport mode changes
    this.templateService.currentMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.isMobile = mode === 3;
        this.cdr.markForCheck();
      });

    // Check if this component is inside a SurveyJS question
    setTimeout(() => {
      this.checkForSurveyJSContext();
    }, 0);
  }

  ngOnInit(): void {
    // Initialize arrays
    this.dataSource = {
      items: [],
      totalCount: 0,
      pageSize: 10,
      pageIndex: 0,
    };
    this.paginatedData$ = new BehaviorSubject<any[]>([]);

    // Initialize settings - preserve existing settings if they exist
    if (!this.settings.columns || this.settings.columns.length === 0) {
      this.settings = {
        columns: [],
        pageable: true,
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
        sortable: true,
        filterable: true,
        rowHover: true,
        striped: true,
        dense: false,
        multiSelect: false,
        defaultSortDirection: 'desc',
      };
    } else {
      // Ensure pageable is set to true for SurveyJS components
      this.settings.pageable = true;
      if (!this.settings.pageSizeOptions) {
        this.settings.pageSizeOptions = [10, 25, 50, 100];
      }
    }

    // For SurveyJS components, ensure pageSizeOptions are properly set from input
    if (this.isSurveyJSComponent) {
      const pageSizeOptionsInput = this.getInput('pageSizeOptions');

      if (pageSizeOptionsInput) {
        try {
          let parsedOptions;
          if (typeof pageSizeOptionsInput === 'string') {
            parsedOptions = JSON.parse(pageSizeOptionsInput);
          } else if (Array.isArray(pageSizeOptionsInput)) {
            parsedOptions = pageSizeOptionsInput;
          } else {
            parsedOptions = JSON.parse(String(pageSizeOptionsInput));
          }

          if (Array.isArray(parsedOptions)) {
            this.settings.pageSizeOptions = parsedOptions
              .map((opt) => Number(opt))
              .filter((opt) => !isNaN(opt));

            // Use the pageSize from settings if it exists, otherwise use the first option
            if (this.settings.pageSize) {
              this.pageSize = this.settings.pageSize;
            } else if (this.settings.pageSizeOptions.length > 0) {
              this.settings.pageSize = this.settings.pageSizeOptions[0];
              this.pageSize = this.settings.pageSizeOptions[0];
            }
          } else {
            throw new Error('pageSizeOptions is not an array');
          }
        } catch (error) {
          this.settings.pageSizeOptions = [10, 25, 50, 100];
          this.settings.pageSize = 10;
          this.pageSize = 10;
        }
      } else {
        this.settings.pageSizeOptions = [10, 25, 50, 100];
        this.settings.pageSize = 10;
        this.pageSize = 10;
      }
    }

    // Load default sort settings from component settings
    const defaultSortColumn = this.getSettingValue('defaultSortColumn');
    const defaultSortDirection = this.getSettingValue('defaultSortDirection');

    // Apply default sort settings if they exist
    if (defaultSortColumn) {
      this.settings.defaultSortColumn = defaultSortColumn;
    }

    if (
      defaultSortDirection &&
      (defaultSortDirection === 'asc' || defaultSortDirection === 'desc')
    ) {
      this.settings.defaultSortDirection = defaultSortDirection as
        | 'asc'
        | 'desc';
    }

    // Set searchable from inputs early so the search bar is shown before store emits (e.g. SurveyJS in modal)
    const enableSearchInput = this.getInput('enableSearch');
    if (enableSearchInput) {
      this.settings.searchable = true;
    }

    // Store the initial route information
    const currentUrl = this.router.url;
    this.lastRouteBase = this.getRouteBase(currentUrl);
    this.lastFullUrl = currentUrl;

    const persistSearchInput = this.getInput('persistSearch');
    if (
      persistSearchInput !== undefined &&
      persistSearchInput !== null &&
      persistSearchInput !== ''
    ) {
      this.persistSearch =
        persistSearchInput === true || persistSearchInput === 'true';
    }
    this.restorePersistedSearch();

    // Initialize pageSizeOptions from input before store subscription can override it
    const pageSizeOptionsInput = this.getInput('pageSizeOptions');
    if (pageSizeOptionsInput) {
      try {
        const parsedOptions =
          typeof pageSizeOptionsInput === 'string'
            ? JSON.parse(pageSizeOptionsInput)
            : pageSizeOptionsInput;
        this.settings.pageSizeOptions = parsedOptions;
      } catch (error) {
        console.error(
          '[DynamicTable] Error parsing pageSizeOptions from input:',
          error
        );
      }
    }

    this.prepareDataSourceConfiguration();
    this.ensureFacetFiltersInitialized();
    this.syncDisplayFilters();

    // Subscribe to input changes

    // Also ensure disableClear is set immediately when MatSort becomes available
    setTimeout(() => {
      if (this.sort) {
        this.sort.disableClear = true;
      }
    }, 0);

    this.store
      .select((state) => state.data)
      .pipe(
        filter(() => {
          const hasDataSource = !!this.getInput('dataSource');
          return hasDataSource;
        }),
        distinctUntilChanged((prev, curr) => {
          const dataSourceKey = this.getInput('dataSource');
          const prevEntry = prev[dataSourceKey];
          const currEntry = curr[dataSourceKey];
          if (!prevEntry || !currEntry) {
            return false;
          }
          return (
            JSON.stringify({
              data: prevEntry.data,
              settings: prevEntry.settings,
              lastUpdated: prevEntry.lastUpdated,
            }) ===
            JSON.stringify({
              data: currEntry.data,
              settings: currEntry.settings,
              lastUpdated: currEntry.lastUpdated,
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((dataState) => {
        const dataSourceKey = this.getInput('dataSource');
        const data = dataState[dataSourceKey];
        const selectionSnapshot = this.getFacetSelectionSnapshot();

        if (dataSourceKey) {
          this.ensureFacetFiltersInitialized();
        }

        if (
          dataSourceKey &&
          isProfileUrlScopedDataSource(dataSourceKey) &&
          data?.data
        ) {
          const profileUuid = this.userService.getCurrentUserUuid();
          const cachedUuid = getCachedDataUserUuid(data.data);
          if (
            profileUuid &&
            cachedUuid &&
            cachedUuid !== profileUuid
          ) {
            this.store.dispatch(
              DataActions.clearData({ dataSourceKey })
            );
            this.initialDataLoadRequested = false;
            this.requestInitialDataLoad(dataSourceKey);
            return;
          }
        }

        if (data?.data || data?.settings) {
          // Update settings first if available
          if (data.settings) {
            // Preserve pageSizeOptions from input if it was set
            const preservedPageSizeOptions = this.settings.pageSizeOptions;

            this.settings = { ...this.settings, ...data.settings };

            if (data.settings.filters?.length) {
              this.settings.filters = this.mergeFiltersPreservingSelections(
                data.settings.filters,
                this.settings.filters,
                this._internalFilters
              );
            }

            // Restore pageSizeOptions if it was set from input and not provided by data source
            if (
              preservedPageSizeOptions &&
              (!data.settings.pageSizeOptions ||
                data.settings.pageSizeOptions.length === 0)
            ) {
              this.settings.pageSizeOptions = preservedPageSizeOptions;
            }

            // Ensure pageable is true for SurveyJS components
            if (this.isSurveyJSComponent) {
              this.settings.pageable = true;
            }

            // Force pageSizeOptions to be set if not already set
            if (
              !this.settings.pageSizeOptions ||
              this.settings.pageSizeOptions.length === 0
            ) {
              // Try to get from input again
              const pageSizeOptionsInput = this.getInput('pageSizeOptions');
              if (pageSizeOptionsInput) {
                try {
                  let parsedOptions;
                  if (typeof pageSizeOptionsInput === 'string') {
                    parsedOptions = JSON.parse(pageSizeOptionsInput);
                  } else if (Array.isArray(pageSizeOptionsInput)) {
                    parsedOptions = pageSizeOptionsInput;
                  } else {
                    parsedOptions = JSON.parse(String(pageSizeOptionsInput));
                  }

                  if (Array.isArray(parsedOptions)) {
                    this.settings.pageSizeOptions = parsedOptions
                      .map((opt) => Number(opt))
                      .filter((opt) => !isNaN(opt));

                    // Use the pageSize from settings if it exists, otherwise use the first option
                    if (this.settings.pageSize) {
                      this.pageSize = this.settings.pageSize;
                    } else if (this.settings.pageSizeOptions.length > 0) {
                      this.settings.pageSize = this.settings.pageSizeOptions[0];
                      this.pageSize = this.settings.pageSizeOptions[0];
                    }
                  }
                } catch (error) {
                  this.settings.pageSizeOptions = [10, 25, 50, 100];
                  this.settings.pageSize = 10;
                  this.pageSize = 10;
                }
              } else {
                this.settings.pageSizeOptions = [10, 25, 50, 100];
                this.settings.pageSize = 10;
                this.pageSize = 10;
              }
            } else {
              // If pageSizeOptions is already set, ensure pageSize matches the first option
              if (this.settings.pageSizeOptions.length > 0) {
                // Use the pageSize from settings if it exists, otherwise use the first option
                if (this.settings.pageSize) {
                  this.pageSize = this.settings.pageSize;
                } else {
                  this.settings.pageSize = this.settings.pageSizeOptions[0];
                  this.pageSize = this.settings.pageSizeOptions[0];
                }
              }
            }

            // Also update component input properties if they are specified in settings
            if (data.settings.persistSelections !== undefined) {
              this.persistSelections = data.settings.persistSelections;
            }
            if (data.settings.selectionKeyField) {
              this.selectionKeyField = data.settings.selectionKeyField;
            }
            if (data.settings.multiSelect !== undefined) {
              this.simpleMultiSelect = data.settings.multiSelect;
            }

            if (!this.tableSettingsInitialized) {
              this.initializeTable();
              this.tableSettingsInitialized = true;
            }
          }

          this.ensureFacetFiltersInitialized();
          this.refreshFacetFiltersFromDataSource(
            data?.data?.facets ?? {},
            selectionSnapshot
          );
          this.syncDisplayFilters();

          // Then handle data
          if (data.data) {
            if (
              this.settings.pageable &&
              typeof data.data === 'object' &&
              'items' in data.data
            ) {
              // Handle paginated response
              this.dataSource = {
                items: data.data.items || [],
                totalCount:
                  data.data.totalCount ?? data.data.items?.length ?? 0,
                pageSize: data.data.pageSize ?? this.pageSize,
                pageIndex: data.data.pageIndex ?? this.pageIndex,
              };
              this.pageSize = this.dataSource.pageSize ?? this.pageSize;
              this.pageIndex = this.dataSource.pageIndex ?? this.pageIndex;
            } else if (Array.isArray(data.data)) {
              // Handle non-paginated response
              this.dataSource = {
                items: data.data,
                totalCount: data.data.length,
                pageSize: this.pageSize,
                pageIndex: this.pageIndex,
              };
            }

            // Initialize module columns visibility based on option state
            const showModuleOption = this.settings.tableOptions?.find(
              (option) => option.id === 'show_module_columns'
            );
            let showModuleColumns = false;

            if (showModuleOption) {
              // Use the explicit state if set in tableOptionsState
              if (this.tableOptionsState[showModuleOption.id] !== undefined) {
                showModuleColumns = this.tableOptionsState[showModuleOption.id];
              }
              // Otherwise use the default specified in the option
              else if (showModuleOption.default !== undefined) {
                showModuleColumns = showModuleOption.default;
                // Also update the state for consistency
                this.tableOptionsState[showModuleOption.id] =
                  showModuleOption.default;
              }
            }

            // Apply column visibility
            this.updateDisplayedColumns(showModuleColumns);

            // IMPORTANT: Ensure the pagination data is properly updated
            this.paginatedData$.next(this.dataSource.items);

            // Force the client-side pagination to be applied
            setTimeout(() => {
              this.applyPagination();
            }, 0);

            this.cdr.detectChanges();
          } else if (data.data === undefined && !data.loading) {
            this.requestInitialDataLoad(dataSourceKey);
          }
        } else if (!dataState[dataSourceKey]) {
          this.requestInitialDataLoad(dataSourceKey);
        } else if (dataSourceKey) {
          this.syncDisplayFilters();
        }
      });

    // Add initialization for persistent selections
    this.persistentSelections = new Map<string, any>();

    // Initialize from input values
    this.simpleMultiSelect = this.getInput('simpleMultiSelect') === true;
    this.selectionKeyField = this.getInput('selectionKeyField') || '';
    this.persistSelections = this.getInput('persistSelections') === true;
    const persistSearchFromInput = this.getInput('persistSearch');
    if (
      persistSearchFromInput !== undefined &&
      persistSearchFromInput !== null &&
      persistSearchFromInput !== ''
    ) {
      this.persistSearch =
        persistSearchFromInput === true || persistSearchFromInput === 'true';
    }

    // Ensure persistent selections map is properly initialized
    if (!this.persistentSelections) {
      this.persistentSelections = new Map<string, any>();
    }

    // Fix paginator selects after initialization
    setTimeout(() => {
      this.fixPaginatorSelects();
    }, 100);
  }

  private initializeTable(): void {
    try {
      const dataSourceKey = this.getInput('dataSource');
      const dsSettings = dataSourceKey
        ? this.dataSourceService.getDataSourceSettings(dataSourceKey)
        : undefined;
      if (dataSourceKey) {
        if (dsSettings) {
          if (
            (!this.settings.filters || this.settings.filters.length === 0) &&
            dsSettings.filters?.length
          ) {
            this.settings.filters = dsSettings.filters;
          }
          if (dsSettings.clientSideFiltering) {
            this.settings.clientSideFiltering = true;
          }
          this.ensureFacetFiltersInitialized();
          if (
            (!this.settings.tableOptions ||
              this.settings.tableOptions.length === 0) &&
            dsSettings.tableOptions?.length
          ) {
            this.settings.tableOptions = dsSettings.tableOptions;
          }
        }
      }

      // Set table headings
      if (this.settings.columns && this.settings.columns.length > 0) {
        // Columns are already set
      }

      // Set table settings from inputs
      const pageable = this.getInput('pageable');
      if (pageable !== undefined) {
        this.settings.pageable = pageable === true;
      }

      const pageSizeOptions = this.getInput('pageSizeOptions');

      if (pageSizeOptions) {
        // Parse JSON string if it's a string, otherwise use as-is
        try {
          let parsedOptions;
          if (typeof pageSizeOptions === 'string') {
            parsedOptions = JSON.parse(pageSizeOptions);
          } else if (Array.isArray(pageSizeOptions)) {
            parsedOptions = pageSizeOptions;
          } else {
            // Try to parse as JSON string if it's not already an array
            parsedOptions = JSON.parse(String(pageSizeOptions));
          }

          // Ensure it's an array of numbers
          if (Array.isArray(parsedOptions)) {
            this.settings.pageSizeOptions = parsedOptions
              .map((opt) => Number(opt))
              .filter((opt) => !isNaN(opt));

            // Use the pageSize from settings if it exists, otherwise use the first option
            if (this.settings.pageSize) {
              this.pageSize = this.settings.pageSize;
            } else if (this.settings.pageSizeOptions.length > 0) {
              this.settings.pageSize = this.settings.pageSizeOptions[0];
              this.pageSize = this.settings.pageSizeOptions[0];
            }
          } else {
            throw new Error('pageSizeOptions is not an array');
          }
        } catch (error) {
          this.settings.pageSizeOptions = [10, 25, 50, 100]; // fallback
        }
      } else {
        this.settings.pageSizeOptions = [10, 25, 50, 100];
      }

      const pageSize = this.getInput('pageSize');
      if (pageSize) {
        this.settings.pageSize = pageSize;
        this.pageSize = pageSize;
      }

      const striped = this.getInput('striped');
      if (striped !== undefined) {
        this.settings.striped = striped === true;
      }

      const sortable = this.getInput('sortable');
      if (sortable !== undefined) {
        this.settings.sortable = sortable === true;
      }

      const rowHover = this.getInput('rowHover');
      if (rowHover !== undefined) {
        this.settings.rowHover = rowHover === true;
      }

      const dense = this.getInput('dense');
      if (dense !== undefined) {
        this.settings.dense = dense === true;
      }

      const multiSelect = this.getInput('multiSelect');
      if (multiSelect !== undefined) {
        this.settings.multiSelect = multiSelect === true;
      }

      // Initialize search if enabled
      const enableSearch = this.getInput('enableSearch');
      if (enableSearch) {
        this.settings.searchable = true;
      }
      // Focus search when focusSearchOnLoad is set (e.g. table in modal); search bar has just been enabled so schedule focus after view updates
      if (this.getSettingValue('focusSearchOnLoad') === true) {
        setTimeout(() => this.tryFocusSearchInput(), 100);
        setTimeout(() => this.tryFocusSearchInput(), 450);
      }

      // Initialize sorting from component settings (data source settings or inputs)
      const defaultSortColumn =
        this.getSettingValue('defaultSortColumn') ??
        this.settings.defaultSortColumn;
      const defaultSortDirection =
        this.getSettingValue('defaultSortDirection') ??
        this.settings.defaultSortDirection;

      if (defaultSortColumn) {
        this.settings.defaultSortColumn = defaultSortColumn;

        // Directly initialize sort filters with default sort column
        this._internalFilters = this._internalFilters.filter(
          (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
        );

        // Add sort column filter
        this._internalFilters.push({
          column: 'sortColumn',
          value: defaultSortColumn,
          operator: 'equals',
          label: 'Sort Column',
          options: [],
        });
      }

      if (
        defaultSortDirection &&
        (defaultSortDirection === 'asc' || defaultSortDirection === 'desc')
      ) {
        this.settings.defaultSortDirection = defaultSortDirection as
          | 'asc'
          | 'desc';

        // Add sort direction filter
        this._internalFilters.push({
          column: 'sortDirection',
          value: defaultSortDirection,
          operator: 'equals',
          label: 'Sort Direction',
          options: [],
        });

        // Set up default sort for backend handling - do not apply client-side sorting
        if (defaultSortColumn) {
          // Ensure MatSort will be initialized with the correct state after view init
          setTimeout(() => {
            this.synchronizeMatSortWithState();
            this.ensureSortStateVisible();
          }, 0);
        }
      }

      // Initialize sorting - set default sort if provided
      const defaultSort = this.getInput('defaultSort');

      // Initialize filters from dynamic input (skip when data source uses server-side facets)
      try {
        const filtersInput = this.getInput('filters');
        const hasFacetConfig = !!dsSettings?.facetFilters?.fields?.length;
        if (filtersInput && !hasFacetConfig) {
          // Parse JSON string if it's a string, otherwise use as-is
          const parsedFilters =
            typeof filtersInput === 'string'
              ? JSON.parse(filtersInput)
              : filtersInput;
          if (Array.isArray(parsedFilters) && parsedFilters.length > 0) {
            // Only use filters that aren't sort or pagination filters
            const visibleFilters = parsedFilters
              .filter(
                (f) =>
                  ![
                    'sortColumn',
                    'sortDirection',
                    'pageSize',
                    'pageIndex',
                  ].includes(f.column)
              )
              .map((filter) => ({
                column: filter.column,
                value: filter.value,
                operator: filter.operator,
                label: filter.label,
                options: filter.options,
              }));

            this.settings.filters = visibleFilters;
          }
        }
      } catch (error) {
        console.error('Error parsing filters:', error);
      }

      this.syncDisplayFilters();

      // Initialize table options with defaults only when not already set (preserve user toggle state across reloads)
      if (this.settings.tableOptions) {
        this.settings.tableOptions.forEach((option) => {
          if (
            option.default !== undefined &&
            this.tableOptionsState[option.id] === undefined
          ) {
            this.tableOptionsState[option.id] = option.default;

            // Apply default effects
            if (option.id === 'show_module_columns') {
              this.toggleModuleColumns(option.default);
            }
          }

          if (this.tableOptionsState[option.id] !== undefined) {
            const optionFilter: TableFilter = {
              column: option.id,
              value: this.tableOptionsState[option.id],
              operator: 'equals',
              label: option.label,
              options: option.options || [],
            };
            const existingIdx = this._internalFilters.findIndex(
              (f) => f.column === option.id
            );
            if (existingIdx >= 0) {
              this._internalFilters[existingIdx] = optionFilter;
            } else {
              this._internalFilters.push(optionFilter);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error initializing table:', error);
    }
  }

  /** Seed empty facet filter chips before the first API facet response arrives. */
  private ensureFacetFiltersInitialized(): void {
    const dataSourceKey = this.getInput('dataSource');
    if (!dataSourceKey) {
      return;
    }

    const dsSettings =
      this.dataSourceService.getDataSourceSettings(dataSourceKey);
    if (!dsSettings?.facetFilters?.fields?.length) {
      return;
    }

    if (!this.settings.filters?.length) {
      const selectionSnapshot = this.getFacetSelectionSnapshot();
      this.settings.filters = mapFacetsToTableFilters(
        {},
        dsSettings.facetFilters,
        selectionSnapshot
      );
    }
    this.syncDisplayFilters();
  }

  /** Push settings.filters to the template-bound displayFilters array. */
  private syncDisplayFilters(): void {
    this.displayFilters = (this.settings.filters ?? []).map((filter) => ({
      column: filter.column,
      value: filter.value,
      operator: filter.operator,
      label: filter.label,
      options: filter.options
        ? filter.options.map((option) => ({ ...option }))
        : [],
    }));
    this.cdr.markForCheck();
  }

  /** Keep facet filter chips in sync with store/API facet data. */
  private refreshFacetFiltersFromDataSource(
    facets: Record<string, FacetOption[]> | undefined | null,
    selectionOverride?: FacetFilterExistingSelection[]
  ): void {
    const dataSourceKey = this.getInput('dataSource');
    if (!dataSourceKey) {
      return;
    }

    const facetConfig = this.dataSourceService.getDataSourceSettings(
      dataSourceKey
    )?.facetFilters as FacetFilterConfig | undefined;
    if (!facetConfig?.fields?.length) {
      return;
    }

    this.applyFacetFiltersFromResponse(
      facets ?? {},
      facetConfig,
      selectionOverride
    );
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const selectableItems = this.getSelectableItems();
    const numSelectableRows = selectableItems.length;

    if (numSelectableRows === 0) {
      return false;
    }

    // If persistent selections are enabled, check if all items on current page are in persistent cache
    if (this.persistSelections && this.selectionKeyField) {
      return selectableItems.every((item) => {
        const key = this.getRowIdentifier(item);
        return this.persistentSelections.has(key);
      });
    }

    // Otherwise use the standard selection model
    const numSelected = this.selection.selected.length;
    return numSelected === numSelectableRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    const selectableItems = this.getSelectableItems();

    if (this.isAllSelected()) {
      // Deselect all items on current page
      if (this.persistSelections && this.selectionKeyField) {
        // Remove items on this page from persistent selections
        for (const item of selectableItems) {
          const idKey = this.getRowIdentifier(item);
          this.persistentSelections.delete(idKey);
        }

        // Update the selection model to match
        this.selection.clear();
        this.updateSelectionFromPersisted();
      } else {
        // Standard behavior - clear all selections
        this.selection.clear();
      }
    } else {
      // Select all items on current page
      if (this.persistSelections && this.selectionKeyField) {
        // Add all items on this page to persistent selections
        for (const item of selectableItems) {
          const idKey = this.getRowIdentifier(item);
          this.persistentSelections.set(idKey, item);
        }

        // Update the selection model to match
        this.updateSelectionFromPersisted();
      } else {
        // Standard behavior - select all items on current page
        this.selection.select(...selectableItems);
      }
    }

    this.emitSelectionChange();
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  /** Toggles a single row selection */
  toggleRowSelection(row: any) {
    this.selection.toggle(row);

    if (this.persistSelections && this.selectionKeyField) {
      const idKey = this.getRowIdentifier(row);

      if (this.selection.isSelected(row)) {
        // Add to persistent selections
        this.persistentSelections.set(idKey, row);
      } else {
        // Remove from persistent selections
        this.persistentSelections.delete(idKey);
      }
    }

    this.emitSelectionChange();
  }

  /** Clears all selections */
  clearSelection() {
    this.selection.clear();

    if (this.persistSelections) {
      this.persistentSelections.clear();
    }

    this.emitSelectionChange();
  }

  /** Gets a unique identifier for a row for persistence */
  private getRowIdentifier(row: any): string {
    // Try to use the selection key field if provided
    if (this.selectionKeyField && row[this.selectionKeyField] !== undefined) {
      return String(row[this.selectionKeyField]);
    }

    // Otherwise fall back to id, guid, or stringified object
    return row.id || row.guid || JSON.stringify(row);
  }

  /** Emits the appropriate selection change event */
  private emitSelectionChange() {
    // If persistent selections are enabled, emit all persisted selections, not just current page
    if (this.persistSelections && this.selectionKeyField) {
      const allPersistentItems = Array.from(this.persistentSelections.values());
      this.rowSelectionChange.emit(allPersistentItems);

      // If we're using simple multi-select with a key field, emit special output
      if (this.simpleMultiSelect) {
        const selectedValues = allPersistentItems
          .map((row) => {
            return row[this.selectionKeyField];
          })
          .filter((val) => val !== undefined);

        this.simpleSelectionChange.emit(selectedValues);
      }
    } else {
      // Standard selection output for all cases
      this.rowSelectionChange.emit(this.selection.selected);

      // If we're using simple multi-select with a key field, emit special output
      if (this.simpleMultiSelect && this.selectionKeyField) {
        const selectedValues = this.selection.selected
          .map((row) => {
            return row[this.selectionKeyField];
          })
          .filter((val) => val !== undefined);

        this.simpleSelectionChange.emit(selectedValues);
      }
    }
  }

  /** Apply batch update to selected rows */
  applyBatchUpdate() {
    if (this.selection.selected.length === 0) {
      return;
    }

    this.isBatchUpdating = true;

    // Track changes for debugging and validation
    const changeLog: Array<{
      row: any;
      key: string;
      oldValue: any;
      newValue: any;
    }> = [];
    const updatedRows: any[] = [];

    // First pass: Apply all updates to row data without calling onChange handlers
    for (const row of this.selection.selected) {
      let rowUpdated = false;
      const originalRow = row;
      let updatedRow = { ...row }; // Create a copy to accumulate all changes

      for (const key in this.batchUpdateValues) {
        // Skip null/undefined/empty values - they indicate "no change" from placeholders
        if (
          this.batchUpdateValues[key] !== undefined &&
          this.batchUpdateValues[key] !== null &&
          this.batchUpdateValues[key] !== ''
        ) {
          const oldValue = row[key];
          const newValue = this.batchUpdateValues[key];

          // Only update if the value has actually changed
          if (oldValue !== newValue) {

            // Apply the change to our updated row copy
            updatedRow[key] = newValue;
            rowUpdated = true;

            // Store the change for onChange handler execution
            changeLog.push({
              row: updatedRow, // Use the updated row for onChange handlers
              key,
              oldValue,
              newValue,
            });
          }
        }
      }

      // If any changes were made to this row, update the data source and selection
      if (rowUpdated) {
        const idx = this.dataSource.items.indexOf(row);
        if (idx !== -1) {
          // Always create a mutable copy of the items array to avoid read-only issues
          const mutableItems = [...this.dataSource.items];

          // Replace the row in the mutable copy with the fully updated version
          mutableItems[idx] = updatedRow;

          // Update the data source with the mutable copy
          this.dataSource.items = mutableItems;

          // Update the selection to use the new row object
          this.selection.deselect(originalRow);
          this.selection.select(updatedRow);
          updatedRows.push(updatedRow);
        }
      }
    }

    if (changeLog.length === 0) {
      this.batchUpdateValues = {};
      this.isBatchUpdating = false;
      return;
    }

    // Group changes by row for efficient onChange handler execution
    const changesByRow = new Map<any, Array<{ key: string; value: any }>>();

    changeLog.forEach((change) => {
      if (!changesByRow.has(change.row)) {
        changesByRow.set(change.row, []);
      }
      changesByRow
        .get(change.row)!
        .push({ key: change.key, value: change.newValue });
    });

    // Second pass: Execute onChange handlers with fully updated row data
    let successfulUpdates = 0;
    const totalUpdates = changesByRow.size;

    changesByRow.forEach((changes, row) => {

      // For event participation updates, we want to call onChange only once per row
      // with all the changes, rather than once per field
      const hasRoleChange = changes.some((c) => c.key === 'role');
      const hasStatusChange = changes.some((c) => c.key === 'status');

      if (hasRoleChange || hasStatusChange) {
        // For role/status changes, call onChange once with the updated row data
        // This ensures the API call includes all the latest values
        const roleColumn = this.settings.columns.find((c) => c.key === 'role');
        if (roleColumn && typeof roleColumn.onChange === 'function') {
          try {
            roleColumn.onChange(row.role, row);
            successfulUpdates++;
          } catch (error) {
            console.error(`[BatchUpdate] Error calling role onChange:`, error);
          }
        }
      } else {
        // For other fields, call onChange individually
        changes.forEach(({ key, value }) => {
          const col = this.settings.columns.find((c) => c.key === key);
          if (col && typeof col.onChange === 'function') {
            try {
              col.onChange(value, row);
              successfulUpdates++;
            } catch (error) {
              console.error(
                `[BatchUpdate] Error calling onChange for ${key}:`,
                error
              );
            }
          } else {
            console.warn(
              `[BatchUpdate] No onChange handler found for column: ${key}`
            );
          }
        });
      }
    });

    // Reset batch values and refresh display
    this.batchUpdateValues = {};
    this.paginatedData$.next([...this.dataSource.items]);
    this.cdr.markForCheck();

    // Clear selection after successful update
    this.clearSelection();

    // Use a shorter delay and provide better feedback
    setTimeout(() => {
      this.isBatchUpdating = false;
      this.cdr.markForCheck();
    }, 500);
  }

  setBatchValue(columnKey: string, value: any) {
    this.batchUpdateValues[columnKey] = value;
  }

  /**
   * Check if there are any valid batch update values set
   */
  hasValidBatchValues(): boolean {
    return Object.values(this.batchUpdateValues).some(
      (value) => value !== undefined && value !== null && value !== ''
    );
  }

  /**
   * Get selectable items (excluding "no data" rows and other non-selectable items)
   * This should work with the currently visible (paginated) items
   */
  private getSelectableItems(): any[] {
    // Use the currently visible items from the paginated data observable
    const currentItems = this.paginatedData$.value || [];
    return currentItems.filter(
      (item) =>
        !item.isNoDataRow &&
        item.participant_id !== 0 &&
        item.name !== 'Der er endnu ingen data'
    );
  }

  /**
   * Load data with filters using either the legacy method or the new standardized API
   */
  private loadDataWithFilters(): void {
    const dataSourceKey = this.getInput('dataSource');

    // Check if we should use the standardized API
    const useStandardizedApi =
      this.getInput('useStandardizedApi') === true || this.useStandardizedApi;
    const apiEndpoint =
      this.getInput('standardizedApiEndpoint') || this.standardizedApiEndpoint;

    if (!dataSourceKey) {
      console.warn(
        '[DynamicTable] loadDataWithFilters: No data source key provided!'
      );
      return;
    }

    // Collect filter parameters
    const filters = [...this._internalFilters];

    // Add pagination filters
    filters.push(
      {
        column: 'pageSize',
        value: this.pageSize,
        operator: 'equals',
        label: 'Page Size',
        options: [],
      },
      {
        column: 'pageIndex',
        value: this.pageIndex,
        operator: 'equals',
        label: 'Page Index',
        options: [],
      }
    );

    // Ensure we have valid sort filters based on settings
    let sortFilters: TableFilter[] = [];

    // First check if there are existing sort filters
    const existingSortColumn = this._internalFilters.find(
      (f) => f.column === 'sortColumn'
    )?.value;
    const existingSortDirection = this._internalFilters.find(
      (f) => f.column === 'sortDirection'
    )?.value;

    // If sort filters exist in internal filters, use those
    if (existingSortColumn && existingSortDirection) {
      sortFilters = [
        {
          column: 'sortColumn',
          value: existingSortColumn,
          operator: 'equals' as const,
          label: 'Sort Column',
          options: [],
        },
        {
          column: 'sortDirection',
          value: existingSortDirection,
          operator: 'equals' as const,
          label: 'Sort Direction',
          options: [],
        },
      ];
    }
    // Otherwise, use the default sort settings if available
    else if (
      this.settings.defaultSortColumn &&
      this.settings.defaultSortDirection
    ) {
      sortFilters = [
        {
          column: 'sortColumn',
          value: this.settings.defaultSortColumn,
          operator: 'equals' as const,
          label: 'Sort Column',
          options: [],
        },
        {
          column: 'sortDirection',
          value: this.settings.defaultSortDirection,
          operator: 'equals' as const,
          label: 'Sort Direction',
          options: [],
        },
      ];

      // Also update internal filters for consistency
      this._internalFilters = [
        ...this._internalFilters.filter(
          (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
        ),
        ...sortFilters,
      ];
    }
    // If no sort is specified but columns are available, default to first sortable column
    else if (this.settings.columns && this.settings.columns.length > 0) {
      const firstSortableColumn = this.settings.columns.find(
        (col) => col.sortable !== false
      );
      if (firstSortableColumn) {
        const defaultSortColumn = firstSortableColumn.key;
        const defaultSortDirection = 'asc';

        // Update settings to reflect the auto-selected sort
        this.settings.defaultSortColumn = defaultSortColumn;
        this.settings.defaultSortDirection = defaultSortDirection;

        // Update the MatSort component to reflect the auto-selected sort
        if (this.sort) {
          this.sort.disableClear = true; // Prevent clearing sort state - always set this first
          this.sort.active = defaultSortColumn;
          this.sort.direction = defaultSortDirection;
        }

        sortFilters = [
          {
            column: 'sortColumn',
            value: defaultSortColumn,
            operator: 'equals' as const,
            label: 'Sort Column',
            options: [],
          },
          {
            column: 'sortDirection',
            value: defaultSortDirection,
            operator: 'equals' as const,
            label: 'Sort Direction',
            options: [],
          },
        ];

        // Also update internal filters for consistency
        this._internalFilters = [
          ...this._internalFilters.filter(
            (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
          ),
          ...sortFilters,
        ];
      }
    }

    // Add search filter if needed
    const searchParams: LoadDataParams = {
      dataSourceKey,
      searchQuery: this.searchText,
      filters: [
        ...filters,
        ...sortFilters,
        ...(this.settings.filters?.filter(
          (f) =>
            f.column !== 'sortColumn' &&
            f.column !== 'sortDirection' &&
            f.value != null &&
            f.value !== '' &&
            !this._internalFilters.some((inf) => inf.column === f.column)
        ) || []),
      ],
    };

    // Add sort parameters if applicable - priority order:
    // 1. MatSort (updated by column clicks)
    // 2. Current custom sort state
    // 3. Default settings

    if (this.sort && this.sort.active && this.sort.direction) {
      searchParams.sort = {
        active: this.sort.active,
        direction: this.sort.direction as 'asc' | 'desc',
      };
    } else if (this.currentSortColumn && this.currentSortDirection) {
      searchParams.sort = {
        active: this.currentSortColumn,
        direction: this.currentSortDirection as 'asc' | 'desc',
      };
    } else if (
      this.settings.defaultSortColumn &&
      this.settings.defaultSortDirection
    ) {
      searchParams.sort = {
        active: this.settings.defaultSortColumn,
        direction: this.settings.defaultSortDirection,
      };
    } else {
    }

    if (useStandardizedApi && apiEndpoint) {
      // Use the standardized API endpoint

      // Load data using the standardized API
      this.dataSourceService
        .loadDataWithStandardFilters(dataSourceKey, apiEndpoint, searchParams)
        .pipe(take(1))
        .subscribe((response) => {

          // Update the store with the response
          this.store.dispatch(
            DataActions.loadDataSuccess({
              dataSourceKey,
              data: response,
              settings:
                response?.settings ??
                this.dataSourceService.getDataSourceSettings(dataSourceKey) ??
                this.settings,
            })
          );

          // Explicitly apply client-side pagination to show correct subset
          this.applyPagination();

          // If we have persistent selections, update the selection model after pagination
          if (this.persistSelections && this.selectionKeyField) {
            setTimeout(() => {
              this.updateSelectionFromPersisted();
            }, 100);
          }

          // Force change detection to update the view
          this.cdr.detectChanges();

          // Ensure MatSort is synchronized with the current sort state
          this.synchronizeMatSortWithState();

          // Force sort state to be visible in the UI
          this.ensureSortStateVisible();
        });
    } else {
      // Use legacy method - dispatch load data
      this.store.dispatch(DataActions.loadData(searchParams));

      // Subscribe to data state changes for this specific data source
      this.store
        .select((state) => state.data[dataSourceKey])
        .pipe(
          takeUntil(this.destroy$),
          filter((dataState) => dataState !== undefined && !dataState.loading),
          take(1) // Take only the first emission after loading is complete
        )
        .subscribe((dataState) => {
          if (dataState && dataState.data) {

            // Make sure we have the latest data in our component
            if (dataState.data.items !== undefined) {
              // Store ALL items in the data source
              this.dataSource.items = [...dataState.data.items];
              if (dataState.data.totalCount !== undefined) {
                this.dataSource.totalCount = dataState.data.totalCount;
              } else {
                this.dataSource.totalCount = dataState.data.items.length;
              }

              // Apply pagination to show correct subset (backend data is already sorted)
              this.applyPagination();

              // If we have persistent selections, update the selection model after pagination
              if (this.persistSelections && this.selectionKeyField) {
                setTimeout(() => {
                  this.updateSelectionFromPersisted();
                }, 100);
              }

              // Force change detection to update the view
              this.cdr.detectChanges();
            } else if (Array.isArray(dataState.data)) {
              // Handle case where data is just an array
              this.dataSource.items = [...dataState.data];
              this.dataSource.totalCount = dataState.data.length;

              // Apply pagination to show correct subset (backend data is already sorted)
              this.applyPagination();

              // If we have persistent selections, update the selection model after pagination
              if (this.persistSelections && this.selectionKeyField) {
                setTimeout(() => {
                  this.updateSelectionFromPersisted();
                }, 100);
              }

              // Force change detection to update the view
              this.cdr.detectChanges();
            }

            // DO NOT re-apply sorting here - backend has already sorted the data

            // Ensure MatSort is synchronized with the current sort state
            this.synchronizeMatSortWithState();

            // Force sort state to be visible in the UI
            this.ensureSortStateVisible();
          } else {
            console.warn(
              '[DynamicTable] No data received from data source:',
              dataSourceKey
            );

            // Add a placeholder row if no data
            this.dataSource.items = [
              {
                isNoDataRow: true,
                message: this.noDataMessage,
              },
            ];
            this.paginatedData$.next(this.dataSource.items);
            this.cdr.detectChanges();
          }
        });
    }
  }

  /**
   * Public refresh method for parent components.
   * Keeps current paging/sort/search state.
   */
  public refreshTable(): void {
    this.loadDataWithFilters();
  }

  private applyPagination(): void {
    // Prevent infinite loops
    if (this.isApplyingPagination) {
      return;
    }

    this.isApplyingPagination = true;

    // Ensure we have data to paginate
    if (!this.dataSource.items || this.dataSource.items.length === 0) {
      this.dataSource.totalCount = 0;
      this.paginatedData$.next([this.createNoDataRow()]);
      this.isApplyingPagination = false;
      this.cdr.markForCheck();
      return;
    }

    // Create deep mutable copies to avoid read-only property issues
    let mutableItems = this.dataSource.items.map((item) => {
      // Create a deep mutable copy of each item
      const mutableItem = JSON.parse(JSON.stringify(item));

      // Ensure all properties are writable by explicitly defining them
      Object.keys(mutableItem).forEach((key) => {
        const value = mutableItem[key];
        Object.defineProperty(mutableItem, key, {
          value: value,
          writable: true,
          enumerable: true,
          configurable: true,
        });
      });

      return mutableItem;
    });

    if (this.settings.clientSideFiltering) {
      mutableItems = this.applyClientSideDisplayFilters(mutableItems);
      this.dataSource.totalCount = mutableItems.length;
    }

    // Check if we need to apply client-side pagination
    // This is true when pagination is enabled and we have server-side pagination
    let paginatedItems = mutableItems;

    // If pagination is disabled, just return without doing anything
    if (!this.settings.pageable) {
      this.isApplyingPagination = false;
      return;
    }

    if (this.settings.pageable) {
      // Check if server already paginated (totalCount > items.length indicates server pagination)
      const isServerPaginated =
        this.dataSource.totalCount &&
        this.dataSource.totalCount > mutableItems.length;

      if (isServerPaginated) {
        // For server-side pagination, we use all the items provided by the server
        // The paginator will show the correct pageSizeOptions from settings
      } else if (mutableItems.length > this.pageSize) {
        // Apply client-side pagination when we have more items than page size
        const startIndex = this.pageIndex * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        paginatedItems = mutableItems.slice(startIndex, endIndex);

        // Update totalCount if it's not set correctly for client-side pagination
        if (
          !this.dataSource.totalCount ||
          this.dataSource.totalCount !== mutableItems.length
        ) {
          this.dataSource.totalCount = mutableItems.length;
        }
      } else {
      }
    } else {
    }

    // Ensure paginatedItems is always an array
    const safeItems = Array.isArray(paginatedItems) ? paginatedItems : [];
    if (safeItems.length === 0) {
      this.dataSource.totalCount = 0;
      this.paginatedData$.next([this.createNoDataRow()]);
    } else {
      this.paginatedData$.next(safeItems);
    }

    // After applying pagination, restore persistent selections if enabled
    if (this.persistSelections && this.selectionKeyField) {
      setTimeout(() => {
        this.updateSelectionFromPersisted();
        this.cdr.detectChanges();
      }, 50);
    }

    this.cdr.markForCheck();
    this.cdr.detectChanges();

    // Reset the flag
    this.isApplyingPagination = false;
  }

  /** Apply active UI filters locally when the data source loads all rows at once. */
  private applyClientSideDisplayFilters(items: any[]): any[] {
    const skip = new Set([
      'sortColumn',
      'sortDirection',
      'pageSize',
      'pageIndex',
    ]);
    let result = items;

    for (const filter of this._internalFilters) {
      if (
        skip.has(filter.column) ||
        filter.value == null ||
        filter.value === ''
      ) {
        continue;
      }

      if (filter.column === 'platform') {
        result = result.filter(
          (row) =>
            (row.platform ?? parsePlatformFromDeviceInfo(row.device_info)) ===
            filter.value
        );
      }
    }

    const search = this.searchText?.trim().toLowerCase();
    if (search) {
      result = result.filter((row) => {
        const usersId = String(row.users_id ?? '').toLowerCase();
        const fullName = String(row.full_name ?? '').toLowerCase();
        const deviceId = String(row.device_identifier ?? '').toLowerCase();
        return (
          usersId.includes(search) ||
          fullName.includes(search) ||
          deviceId.includes(search)
        );
      });
    }

    return result;
  }

  /**
   * Snapshot of active facet selections from UI state and internal filters.
   */
  private getFacetSelectionSnapshot(): FacetFilterExistingSelection[] {
    const skip = new Set([
      'sortColumn',
      'sortDirection',
      'pageSize',
      'pageIndex',
    ]);
    const byColumn = new Map<string, FacetFilterExistingSelection>();

    for (const f of this.settings.filters ?? []) {
      if (!skip.has(f.column) && f.value != null && f.value !== '') {
        byColumn.set(f.column, { column: f.column, value: String(f.value) });
      }
    }
    for (const f of this._internalFilters) {
      if (!skip.has(f.column) && f.value != null && f.value !== '') {
        byColumn.set(f.column, { column: f.column, value: String(f.value) });
      }
    }

    return Array.from(byColumn.values());
  }

  /** Keep user-selected filter values when store/settings refresh replaces filter config. */
  private mergeFiltersPreservingSelections(
    incomingFilters: TableFilter[],
    currentFilters?: TableFilter[],
    internalFilters?: TableFilter[]
  ): TableFilter[] {
    const valueByColumn = new Map<string, string>();

    for (const filter of internalFilters ?? []) {
      if (filter.value != null && filter.value !== '') {
        valueByColumn.set(filter.column, String(filter.value));
      }
    }
    for (const filter of currentFilters ?? []) {
      if (
        filter.value != null &&
        filter.value !== '' &&
        !valueByColumn.has(filter.column)
      ) {
        valueByColumn.set(filter.column, String(filter.value));
      }
    }

    return incomingFilters.map((filter) => {
      const preservedValue = valueByColumn.get(filter.column);
      return {
        ...filter,
        value: preservedValue !== undefined ? preservedValue : filter.value,
        options: filter.options
          ? filter.options.map((option) => ({ ...option }))
          : [],
      };
    });
  }

  /** mat-select compareWith — facet values from API are strings. */
  compareFilterValues = (a: unknown, b: unknown): boolean => {
    const normalize = (value: unknown): string => {
      if (value == null || value === '') {
        return '';
      }
      return String(value);
    };
    return normalize(a) === normalize(b);
  };

  /**
   * Refresh facet filter dropdown options from API response without clearing selected values.
   */
  private applyFacetFiltersFromResponse(
    facets: Record<string, FacetOption[]>,
    facetConfig: FacetFilterConfig,
    selectionOverride?: FacetFilterExistingSelection[]
  ): void {
    const updatedFilters = mapFacetsToTableFilters(
      facets,
      facetConfig,
      selectionOverride ?? this.getFacetSelectionSnapshot()
    );
    if (updatedFilters.length) {
      this.settings.filters = updatedFilters;
      this.syncDisplayFilters();
    }
  }

  // Method to handle filter changes
  onFilterChange(filter: TableFilter, newValue?: any): void {
    const resolvedValue =
      newValue !== undefined
        ? newValue != null && newValue !== ''
          ? String(newValue)
          : ''
        : filter.value == null || filter.value === ''
        ? ''
        : String(filter.value);

    if (this.settings.filters?.length) {
      this.settings.filters = this.settings.filters.map((f) =>
        f.column === filter.column ? { ...f, value: resolvedValue } : { ...f }
      );
    }
    this.syncDisplayFilters();

    if (resolvedValue === '') {
      this._internalFilters = this._internalFilters.filter(
        (f) => f.column !== filter.column
      );
    } else {
      const filterEntry: TableFilter = {
        column: filter.column,
        value: resolvedValue,
        operator: filter.operator,
        label: filter.label,
        options: filter.options
          ? filter.options.map((option) => ({ ...option }))
          : [],
      };
      const existingIdx = this._internalFilters.findIndex(
        (f) => f.column === filter.column
      );
      if (existingIdx >= 0) {
        this._internalFilters[existingIdx] = filterEntry;
      } else {
        this._internalFilters.push(filterEntry);
      }
    }

    // Reset to first page when filtering
    this.pageIndex = 0;

    if (this.settings.clientSideFiltering) {
      this.applyPagination();
    }

    // Apply the filter through the data loading
    this.loadDataWithFilters();
  }

  // Method to handle search changes
  onSearch(searchValue: string): void {
    this.searchText = searchValue;
    this.persistSearchText();

    // Reset to first page when searching
    this.pageIndex = 0;

    // Trigger the search with debounce through the subject
    this.searchSubject.next(this.searchText);

    // After search is performed, ensure pagination is correctly applied
    setTimeout(() => {
      this.applyPagination();

      // Also restore persistent selections if enabled
      if (this.persistSelections && this.selectionKeyField) {
        this.updateSelectionFromPersisted();
      }

      this.cdr.detectChanges();
    }, 800); // Slightly longer timeout to account for debounce
  }

  public onActionClick(
    actionOrColumn: ((row: any) => void) | TableColumn,
    row: any
  ): void {
    if (typeof actionOrColumn === 'function') {
      actionOrColumn(row);
      return;
    }
    const column = actionOrColumn;
    if (column.opensRowDetailsModal) {
      this.openRowDetailsModal(row);
      return;
    }
    if (column.action) {
      column.action(row);
    }
  }

  onCellClick(event: Event, column: TableColumn, row: any): void {
    if (column.onClick) {
      event.stopPropagation(); // Prevent row click from firing
      column.onClick(row);
    }
  }

  onPageChange(event: PageEvent): void {

    // Update pagination state
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    // Check if we should use client-side pagination
    // This happens when totalCount equals items.length, indicating all data is loaded
    const hasAllData =
      this.dataSource.totalCount &&
      this.dataSource.items &&
      this.dataSource.totalCount === this.dataSource.items.length;

    if (hasAllData) {
      // Apply client-side pagination immediately
      this.applyPagination();

      // Restore persistent selections if enabled
      if (this.persistSelections && this.selectionKeyField) {
        setTimeout(() => {
          this.updateSelectionFromPersisted();
          this.cdr.detectChanges();
        }, 50);
      }
    } else {
      // Standard behavior for server-side pagination
      this.loadDataWithFilters();

      // After server data loads, restore persistent selections if enabled
      if (this.persistSelections && this.selectionKeyField) {
        setTimeout(() => {
          this.updateSelectionFromPersisted();
          this.cdr.detectChanges();
        }, 100);
      }
    }
  }

  isValidDate(value: any): boolean {
    if (!value) return false;
    const date = new Date(value);
    return date instanceof Date && !isNaN(date.getTime());
  }

  ngAfterViewInit() {

    // CRITICAL: Always disable clear on MatSort component regardless of other conditions
    if (this.sort) {
      this.sort.disableClear = true;
    }

    // Initialize default sort after view is ready
    setTimeout(() => {
      this.initializeDefaultSort();
    }, 100);

    // Add SurveyJS detection logic with delay to allow SurveyJS widget to set up inputs
    setTimeout(() => {
      this.checkForSurveyJSContext();
    }, 100);

    // Fix paginator selects after view init
    setTimeout(() => {
      this.fixPaginatorSelects();
    }, 200);

    // Ensure sort headers are properly initialized
    setTimeout(() => {
      this.ensureSortHeadersInitialized();
    }, 400);

    // Focus search input when focusSearchOnLoad is enabled (e.g. for tables in modals)
    // Don't require settings.searchable here: on first load it's set later by initializeTable() when store emits
    const focusSearch = this.getSettingValue('focusSearchOnLoad');
    if (focusSearch === true) {
      const tryFocusSearch = () => {
        if (!this.settings?.searchable) return false;
        this.cdr.detectChanges();
        const el = this.searchInputRef?.nativeElement;
        if (el && typeof el.focus === 'function') {
          el.focus();
          return true;
        }
        return false;
      };
      // Poll until search bar exists (settings.searchable can become true later when store/initializeTable runs)
      const intervalMs = 200;
      const maxAttempts = 25; // 5 seconds total
      let attempts = 0;
      this.focusSearchPollingId = setInterval(() => {
        attempts++;
        if (tryFocusSearch() || attempts >= maxAttempts) {
          if (this.focusSearchPollingId != null) {
            clearInterval(this.focusSearchPollingId);
            this.focusSearchPollingId = null;
          }
        }
      }, intervalMs);
    }
  }

  /**
   * Try to focus the search input. Call when focusSearchOnLoad is true and searchable may have just been set (e.g. from initializeTable).
   */
  private tryFocusSearchInput(): boolean {
    if (
      this.getSettingValue('focusSearchOnLoad') !== true ||
      !this.settings?.searchable
    )
      return false;
    this.cdr.detectChanges();
    const el = this.searchInputRef?.nativeElement;
    if (el && typeof el.focus === 'function') {
      el.focus();
      return true;
    }
    return false;
  }

  // Apply sorting locally to the client-side data for immediate feedback
  // NOTE: This method is now disabled as we rely on backend sorting instead
  // to avoid conflicts with server-side pagination
  private applySortLocally(column: string, direction: string): void {
    return;

    /*
    // This code is commented out to prevent client-side sorting conflicts
    // Make a copy of the current data
    const data = [...this.dataSource.items];
    
    if (!column || !direction || data.length === 0) {
        return;
    }
    
    
    // Check if column exists in data before sorting
    const columnExists = data.length > 0 && column in data[0];
    if (!columnExists) {
        console.warn(`[DynamicTable] Sort column '${column}' does not exist in data objects`);
        return;
    }
    
    // Sort the data
    data.sort((a, b) => {
        const aValue = a[column];
        const bValue = b[column];
        
        // Handle undefined or null values
        if (aValue === undefined || aValue === null) {
            return direction === 'asc' ? -1 : 1;
        }
        if (bValue === undefined || bValue === null) {
            return direction === 'asc' ? 1 : -1;
        }
        
        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return direction === 'asc' 
                ? aValue.localeCompare(bValue) 
                : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return direction === 'asc' 
                ? aValue - bValue 
                : bValue - aValue;
        } else if (aValue instanceof Date && bValue instanceof Date) {
            return direction === 'asc' 
                ? aValue.getTime() - bValue.getTime() 
                : bValue.getTime() - aValue.getTime();
        }
        
        // Fall back to string comparison
        const aStr = String(aValue);
        const bStr = String(bValue);
        return direction === 'asc' 
            ? aStr.localeCompare(bStr) 
            : bStr.localeCompare(aStr);
    });
    
    // First, ensure the sort header shows the correct arrow
    // This happens before the data update to minimize perceived lag
    if (this.sort) {
        this.sort.active = column;
        this.sort.direction = direction as 'asc' | 'desc';
        this.sort.disableClear = true; // Prevent clearing sort state
    }
    
    // Use requestAnimationFrame to ensure DOM updates happen on the next frame for smoother transitions
    requestAnimationFrame(() => {
        // Update the data source with sorted data
        this.dataSource.items = data;
        
        // Apply pagination to show the correct subset of sorted data
        this.applyPagination();
        
        this.cdr.detectChanges();
    });
    */
  }

  ngOnDestroy(): void {
    if (this.focusSearchPollingId != null) {
      clearInterval(this.focusSearchPollingId);
      this.focusSearchPollingId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Custom pagination methods
  get Math() {
    return Math;
  }

  getTotalPages(): number {
    const total = this.getResultTotalCount();
    if (total === 0) {
      return 0;
    }
    return Math.ceil(total / this.pageSize);
  }

  getResultTotalCount(): number {
    if (this.dataSource.totalCount != null) {
      return this.dataSource.totalCount;
    }
    return this.dataSource.items?.length ?? 0;
  }

  getPaginationStart(): number {
    const total = this.getResultTotalCount();
    if (total === 0) {
      return 0;
    }
    return this.pageIndex * this.pageSize + 1;
  }

  getPaginationEnd(): number {
    const total = this.getResultTotalCount();
    if (total === 0) {
      return 0;
    }
    return Math.min((this.pageIndex + 1) * this.pageSize, total);
  }

  private createNoDataRow(): { isNoDataRow: true; message: string } {
    return {
      isNoDataRow: true,
      message: this.noDataMessage,
    };
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newPageSize = parseInt(target.value);

    // Create a PageEvent-like object
    const pageEvent: PageEvent = {
      pageIndex: 0, // Reset to first page when changing page size
      pageSize: newPageSize,
      length: this.getResultTotalCount(),
    };

    this.onPageChange(pageEvent);
  }

  private isPersistSearchEnabled(): boolean {
    return this.persistSearch === true;
  }

  private getSearchStorageKey(): string | null {
    const dataSourceKey = this.getInput('dataSource');
    if (!dataSourceKey || !this.isPersistSearchEnabled()) {
      return null;
    }
    const routeBase = this.getRouteBase(this.router.url);
    return `${DynamicTableComponent.SEARCH_STORAGE_PREFIX}${dataSourceKey}.${routeBase}`;
  }

  private restorePersistedSearch(): void {
    const key = this.getSearchStorageKey();
    if (!key || !isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        this.searchText = stored;
      }
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }

  private persistSearchText(): void {
    const key = this.getSearchStorageKey();
    if (!key || !isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      const trimmed = this.searchText.trim();
      if (trimmed) {
        sessionStorage.setItem(key, trimmed);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore storage errors
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.persistSearchText();
    this.onSearch('');
  }

  get searchInputName(): string {
    const dataSourceKey = this.getInput('dataSource');
    return `dynamic-table-search-${dataSourceKey || 'default'}`;
  }

  /**
   * Extracts the base route from a URL (e.g., '/lokation' from '/lokation/some-uuid')
   * @param url The full URL
   * @returns The base route
   */
  private getRouteBase(url: string): string {
    // Parse the URL
    const urlObj = new URL(url, window.location.origin);

    // Split the pathname into segments
    const pathSegments = urlObj.pathname
      .split('/')
      .filter((segment) => segment);

    // If there are no segments, return the empty string
    if (pathSegments.length === 0) {
      return '';
    }

    // Return the first segment (the base route)
    return pathSegments[0];
  }

  /**
   * Apply data-source settings and default sort without triggering an API call.
   */
  private prepareDataSourceConfiguration(): void {
    const dataSourceKey = this.getInput('dataSource');
    if (!dataSourceKey) {
      return;
    }

    const dsSettings =
      this.dataSourceService.getDataSourceSettings(dataSourceKey);
    if (dsSettings) {
      this.settings = { ...this.settings, ...dsSettings };
      this.ensureFacetFiltersInitialized();
      this.refreshFacetFiltersFromDataSource({});
      this.syncDisplayFilters();
      dsSettings.tableOptions?.forEach((option: TableOption) => {
        if (
          option.default !== undefined &&
          this.tableOptionsState[option.id] === undefined
        ) {
          this.tableOptionsState[option.id] = option.default;
        }
      });
      this.cdr.markForCheck();
    }

    this.applyDefaultSortFilters();
  }

  private applyDefaultSortFilters(): void {
    let sortColumn = this.settings.defaultSortColumn;
    let sortDirection = this.settings.defaultSortDirection;

    if (
      !sortColumn &&
      this.settings.columns &&
      this.settings.columns.length > 0
    ) {
      const firstSortableColumn = this.settings.columns.find(
        (col) => col.sortable !== false
      );
      if (firstSortableColumn) {
        sortColumn = firstSortableColumn.key;
        sortDirection = 'asc';
        this.settings.defaultSortColumn = sortColumn;
        this.settings.defaultSortDirection = sortDirection;
      }
    }

    if (sortColumn && sortDirection && this.sort) {
      this.sort.disableClear = true;
      this.sort.active = sortColumn;
      this.sort.direction = sortDirection;
    }

    if (sortColumn && sortDirection) {
      const sortFilters: TableFilter[] = [
        {
          column: 'sortColumn',
          value: sortColumn,
          operator: 'equals',
          label: 'Sort Column',
          options: [],
        },
        {
          column: 'sortDirection',
          value: sortDirection,
          operator: 'equals',
          label: 'Sort Direction',
          options: [],
        },
      ];

      this._internalFilters = [
        ...this._internalFilters.filter(
          (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
        ),
        ...sortFilters,
      ];
    }
  }

  private requestInitialDataLoad(dataSourceKey: string): void {
    if (this.initialDataLoadRequested || !dataSourceKey) {
      return;
    }

    let isLoading = false;
    this.store
      .select((state) => state.data[dataSourceKey]?.loading === true)
      .pipe(take(1))
      .subscribe((loading) => {
        isLoading = loading;
      });

    if (isLoading) {
      return;
    }

    this.initialDataLoadRequested = true;
    this.store.dispatch(
      DataActions.loadData({
        dataSourceKey,
        searchQuery: this.searchText,
        filters: this._internalFilters.map((filter) => ({ ...filter })),
      })
    );
  }

  /**
   * Forces a reload of the data source
   */
  private reloadDataSource(): void {
    const dataSourceKey = this.getInput('dataSource');
    if (!dataSourceKey) {
      return;
    }

    this.prepareDataSourceConfiguration();

    const sortColumn = this.settings.defaultSortColumn;
    const sortDirection = this.settings.defaultSortDirection;

    if (sortColumn && sortDirection && this.sort) {
      this.sort.disableClear = true;
      this.sort.active = sortColumn;
      this.sort.direction = sortDirection;

      setTimeout(() => {
        if (this.sort) {
          const sortEvent = {
            active: sortColumn,
            direction: sortDirection,
            isSynthetic: true,
          };
          this.onSortChange(sortEvent);

          setTimeout(() => {
            this.forceSortArrowVisibility(sortColumn, sortDirection);
            this.ensureSortHeadersInitialized();
          }, 200);
        }
      }, 100);

      setTimeout(() => {
        if (this.sort && sortColumn && sortDirection) {
          this.updateSortHeaderClasses(sortColumn, sortDirection);
          this.forceSortArrowVisibility(sortColumn, sortDirection);
        }
      }, 200);
    }

    this.initialDataLoadRequested = true;
    this.loadDataWithFilters();
  }

  // Handle table option change
  onTableOptionChange(option: TableOption, value: any) {
    this.tableOptionsState[option.id] = value;

    // Call custom handler if provided
    if (option.onChange) {
      option.onChange(value);
    }

    // Special handling for specific options
    if (option.id === 'show_module_columns') {
      // Direct handling of columns visibility
      this.updateDisplayedColumns(value);

      // Also use the regular toggle method to update filters
      this.toggleModuleColumns(value);
    } else {
      const optionFilter: TableFilter = {
        column: option.id,
        value: value,
        operator: 'equals',
        label: option.label,
        options: option.options || [],
      };

      if (value === null || value === undefined || value === '') {
        this._internalFilters = this._internalFilters.filter(
          (f) => f.column !== option.id
        );
      } else if (this._internalFilters.some((f) => f.column === option.id)) {
        this._internalFilters = this._internalFilters.map((f) =>
          f.column === option.id ? optionFilter : f
        );
      } else {
        this._internalFilters.push(optionFilter);
      }

      this.pageIndex = 0;
      this.loadDataWithFilters();
    }
  }

  // Toggle module-specific columns
  private toggleModuleColumns(show: boolean) {

    // Add the option to filters so data source can respond
    const optionFilter: TableFilter = {
      column: 'show_module_columns',
      value: show,
      operator: 'equals',
      label: 'Show Module Columns',
      options: [],
    };

    // Add or update the filter
    if (this._internalFilters.some((f) => f.column === 'show_module_columns')) {
      this._internalFilters = this._internalFilters.map((f) =>
        f.column === 'show_module_columns' ? optionFilter : f
      );
    } else {
      this._internalFilters.push(optionFilter);
    }

    // Update the displayed columns based on the toggle state
    this.updateDisplayedColumns(show);

    // Reload data to get fresh data with module columns from the data source
    this.loadDataWithFilters();
  }

  /**
   * Update displayed columns based on module visibility
   */
  private updateDisplayedColumns(showModuleColumns: boolean) {
    // First determine which columns should be displayed
    let columnsToDisplay = this.settings.columns;

    if (!showModuleColumns) {
      // Filter out module columns if they should be hidden
      columnsToDisplay = this.settings.columns.filter(
        (col) => !(col as any).isModuleColumn
      );
    }

    // Remove attempt to modify column width as objects might be frozen
    // Instead, we'll handle button column width in the template or CSS

    // Then update the displayed columns array
    if (this.settings.multiSelect) {
      this.displayedColumns = [
        'select',
        ...columnsToDisplay.map((col) => col.key),
      ];
    } else {
      this.displayedColumns = columnsToDisplay.map((col) => col.key);
    }

  }

  /** Check if a column is a module column */
  isModuleColumn(column: any): boolean {
    return column.isModuleColumn === true;
  }

  /** Check if a column is a button column */
  isButtonColumn(column: any): boolean {
    const type = column.type as string;
    return type === 'button' || type === 'icon-button';
  }

  getColumnWidthStyles(column: TableColumn): {
    [key: string]: string | undefined;
  } {
    return {
      width: column.width,
      'min-width': column.minWidth,
      'max-width': column.maxWidth,
    };
  }

  getColumnCellClasses(
    column: TableColumn,
    extra: { [key: string]: boolean } = {}
  ): { [key: string]: boolean } {
    return {
      'button-column': this.isButtonColumn(column),
      'column-fit-content': !!column.fitContent,
      'column-fill': !!column.fill && !column.fitContent,
      ...extra,
    };
  }

  get useCardLayout(): boolean {
    if (this.settings.layout === 'card') {
      return true;
    }
    if (this.settings.layout === 'table') {
      return false;
    }
    return this.isMobile;
  }

  /** Check if module columns should be visible */
  public isModuleColumnVisible(): boolean {
    const showModuleOption = this.settings.tableOptions?.find(
      (option) => option.id === 'show_module_columns'
    );
    return showModuleOption
      ? this.tableOptionsState[showModuleOption.id] === true
      : false;
  }

  /**
   * Get a setting value from either inputs or settings object
   */
  private getSettingValue(key: string): any {
    // In this component, we might need to access settings in multiple ways

    // Try to get the setting from the component inputs
    const settingInput = this.getInput(key);
    if (settingInput !== undefined) {
      return settingInput;
    }

    // Also try to get it from the 'settings' input if it exists
    const settings = this.getInput('settings');
    if (settings && typeof settings === 'object' && key in settings) {
      const settingValue =
        settings[key]?.value !== undefined
          ? settings[key].value
          : settings[key];
      return settingValue;
    }

    return null;
  }

  onSortChange(event: any): void {

    // Check if this is a synthetic event just to show the arrow
    const isSyntheticEvent = event && event.isSynthetic === true;

    // Always process MatSort changes since we now have proper MatSort integration

    // Only reset to first page for actual user-triggered sort changes
    if (!isSyntheticEvent) {
      this.pageIndex = 0;
    }

    // Keep the other (non-sort) internal filters
    const otherFilters = this._internalFilters.filter(
      (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
    );

    // Handle sort state - trust Angular Material's behavior but ensure we always have a valid direction
    if (event.active && event.direction) {
      // Active sort with valid direction - update the settings to reflect the new sort
      this.settings.defaultSortColumn = event.active;
      this.settings.defaultSortDirection = event.direction;

      // IMPORTANT: Also update our custom sort state to stay in sync
      this.currentSortColumn = event.active;
      this.currentSortDirection = event.direction;
      this.isCustomSortActive = true; // Mark that we have an active sort

      // Create new sort filters
      const sortFilters: TableFilter[] = [
        {
          column: 'sortColumn',
          value: event.active,
          operator: 'equals',
          label: 'Sort Column',
          options: [],
        },
        {
          column: 'sortDirection',
          value: event.direction,
          operator: 'equals',
          label: 'Sort Direction',
          options: [],
        },
      ];

      // Update internal filters with new sort filters
      this._internalFilters = [...otherFilters, ...sortFilters];

    } else if (event.active && !event.direction) {
      // Active column but no direction - this can happen when disableClear doesn't work properly
      // Default to 'asc' for new columns, toggle for same column
      let newDirection: 'asc' | 'desc' = 'asc';

      if (event.active === this.currentSortColumn) {
        // Same column clicked - toggle direction
        newDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Different column - start with asc
      }

      // Update our state
      this.currentSortColumn = event.active;
      this.currentSortDirection = newDirection;
      this.settings.defaultSortColumn = event.active;
      this.settings.defaultSortDirection = newDirection;

      // Update MatSort to reflect the correct direction
      if (this.sort) {
        this.sort.active = event.active;
        this.sort.direction = newDirection;
        this.sort.disableClear = true;
      }

      // Create sort filters
      const sortFilters: TableFilter[] = [
        {
          column: 'sortColumn',
          value: event.active,
          operator: 'equals',
          label: 'Sort Column',
          options: [],
        },
        {
          column: 'sortDirection',
          value: newDirection,
          operator: 'equals',
          label: 'Sort Direction',
          options: [],
        },
      ];

      this._internalFilters = [...otherFilters, ...sortFilters];

    } else {
      // No active column - maintain current sort or use default
      console.warn(
        '[DynamicTable] Invalid sort event, maintaining current sort'
      );

      let columnToUse =
        this.currentSortColumn || this.settings.defaultSortColumn;
      let directionToUse =
        this.currentSortDirection ||
        this.settings.defaultSortDirection ||
        'asc';

      // If still no column, use first sortable column
      if (!columnToUse && this.settings.columns) {
        const firstSortableColumn = this.settings.columns.find(
          (col) => col.sortable !== false
        );
        if (firstSortableColumn) {
          columnToUse = firstSortableColumn.key;
          directionToUse = 'asc';
        }
      }

      if (columnToUse && directionToUse) {
        const sortFilters: TableFilter[] = [
          {
            column: 'sortColumn',
            value: columnToUse,
            operator: 'equals',
            label: 'Sort Column',
            options: [],
          },
          {
            column: 'sortDirection',
            value: directionToUse,
            operator: 'equals',
            label: 'Sort Direction',
            options: [],
          },
        ];
        this._internalFilters = [...otherFilters, ...sortFilters];
      } else {
        this._internalFilters = otherFilters;
      }
    }

    // Use sort filters but don't display them in the UI
    if (this.settings.filters?.length) {
      this.settings.filters = this.settings.filters.filter(
        (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
      );
    }

    // Only reload data for actual user-triggered sort changes
    if (!isSyntheticEvent) {
      // Load data with the updated filters - backend will handle sorting
      this.loadDataWithFilters();
    } else {
    }

    const activeColumn = this.currentSortColumn;
    const activeDirection = this.currentSortDirection;
    if (
      activeColumn &&
      (activeDirection === 'asc' || activeDirection === 'desc')
    ) {
      if (this.sort) {
        this.sort.disableClear = true;
        this.sort.active = activeColumn;
        this.sort.direction = activeDirection;
      }
      this.applyActiveSortHeaderVisual(activeColumn, activeDirection);
    }

    // Ensure the sort arrow remains visible after the sort change
    setTimeout(() => {
      this.ensureSortStateVisible();
    }, 200);
  }

  /**
   * Gets the current sort column from custom state or internal filters
   */
  getCurrentSortColumn(): string {
    // Check internal state first (most reliable)
    if (this.currentSortColumn) {
      return this.currentSortColumn;
    }

    // Check settings second
    if (this.settings?.defaultSortColumn) {
      return this.settings.defaultSortColumn;
    }

    // Auto-select first sortable column if nothing is set
    if (
      this.settings?.sortable &&
      this.settings?.columns &&
      this.settings.columns.length > 0
    ) {
      const firstSortableColumn = this.settings.columns.find(
        (col) => col.sortable !== false
      );
      if (firstSortableColumn) {
        return firstSortableColumn.key;
      }
    }

    return '';
  }

  /**
   * Gets the current sort direction from custom state or internal filters
   */
  getCurrentSortDirection(): 'asc' | 'desc' | '' {
    // Check internal state first (most reliable)
    if (this.currentSortDirection) {
      return this.currentSortDirection;
    }

    // Check settings second
    if (this.settings?.defaultSortDirection) {
      return this.settings.defaultSortDirection;
    }

    // Default to 'asc' if we have a sort column but no direction
    const sortColumn =
      this.currentSortColumn || this.settings?.defaultSortColumn;
    if (sortColumn) {
      return 'asc';
    }

    return '';
  }

  /**
   * Synchronizes the MatSort component state with the current internal sort state
   */
  private synchronizeMatSortWithState(): void {
    // If we already have custom sort state set, preserve it and don't override
    if (this.currentSortColumn && this.currentSortDirection) {

      // Ensure MatSort reflects the current state
      if (this.sort) {
        this.sort.disableClear = true;
        this.sort.active = this.currentSortColumn;
        this.sort.direction = this.currentSortDirection;

        // Force the sort header to update its visual state
        setTimeout(() => {
          this.cdr.detectChanges();
          // Also force the sort arrow to appear
          if (
            this.currentSortDirection === 'asc' ||
            this.currentSortDirection === 'desc'
          ) {
            this.forceSortArrowVisibility(
              this.currentSortColumn,
              this.currentSortDirection
            );
          }
        }, 0);
      }
      return;
    }

    // Get current sort filters from internal filters
    const sortColumnFilter = this._internalFilters.find(
      (f) => f.column === 'sortColumn'
    );
    const sortDirectionFilter = this._internalFilters.find(
      (f) => f.column === 'sortDirection'
    );

    if (sortColumnFilter && sortDirectionFilter) {
      // We have active sort - set custom state to match
      const currentActive = sortColumnFilter.value;
      const currentDirection = sortDirectionFilter.value as 'asc' | 'desc';

      this.currentSortColumn = currentActive;
      this.currentSortDirection = currentDirection;

      // Update MatSort to match
      if (this.sort) {
        this.sort.disableClear = true;
        this.sort.active = currentActive;
        this.sort.direction = currentDirection;
      }

      // Force the sort header to update its visual state
      setTimeout(() => {
        this.cdr.detectChanges();
        // Also force the sort arrow to appear
        if (currentDirection === 'asc' || currentDirection === 'desc') {
          this.forceSortArrowVisibility(currentActive, currentDirection);
        }
      }, 0);
    } else if (
      this.settings.defaultSortColumn &&
      this.settings.defaultSortDirection
    ) {
      // Use default sort if no active sort but defaults are available
      this.currentSortColumn = this.settings.defaultSortColumn;
      this.currentSortDirection = this.settings.defaultSortDirection;

      // Update MatSort to match
      if (this.sort) {
        this.sort.disableClear = true;
        this.sort.active = this.settings.defaultSortColumn;
        this.sort.direction = this.settings.defaultSortDirection;
      }

      // Force the sort header to update its visual state
      setTimeout(() => {
        this.cdr.detectChanges();
        // Also force the sort arrow to appear
        if (
          this.settings.defaultSortColumn &&
          (this.settings.defaultSortDirection === 'asc' ||
            this.settings.defaultSortDirection === 'desc')
        ) {
          this.forceSortArrowVisibility(
            this.settings.defaultSortColumn,
            this.settings.defaultSortDirection
          );
        }
      }, 0);
    } else {
    }
  }

  /**
   * Ensures the sort state is visible in the UI by forcing a refresh of the sort headers
   */
  private ensureSortStateVisible(): void {
    // Use a longer timeout to ensure the DOM has been updated
    setTimeout(() => {
      if (this.currentSortColumn && this.currentSortDirection) {

        // Force change detection to update the visual state
        this.cdr.detectChanges();

        // Also try to force the MatSort component to update its visual state
        if (this.sort) {
          // Use our new method to force sort arrow visibility
          if (
            this.currentSortDirection === 'asc' ||
            this.currentSortDirection === 'desc'
          ) {
            this.forceSortArrowVisibility(
              this.currentSortColumn,
              this.currentSortDirection
            );
          }
        }
      }
    }, 100);
  }

  private findSortHeaderElement(sortColumn: string): HTMLElement | null {
    const host = this.elementRef?.nativeElement as HTMLElement | undefined;
    if (!host || !sortColumn) {
      return null;
    }

    return (
      (host.querySelector(
        `[data-sort-column="${sortColumn}"]`
      ) as HTMLElement | null) ||
      (host.querySelector(
        `[mat-sort-header="${sortColumn}"]`
      ) as HTMLElement | null)
    );
  }

  private getSortHeaderElements(): Element[] {
    const host = this.elementRef?.nativeElement as HTMLElement | undefined;
    if (!host) {
      return [];
    }

    return Array.from(
      host.querySelectorAll('th[data-sort-column], th[mat-sort-header]')
    );
  }

  /** Clear sort styling from every header so only one column can show an arrow. */
  private clearAllSortHeaderVisuals(): void {
    this.getSortHeaderElements().forEach((header) => {
      const headerElement = header as HTMLElement;
      headerElement.classList.remove('mat-sort-header-sorted');
      headerElement.setAttribute('aria-sort', 'none');

      headerElement
        .querySelectorAll('.mat-sort-header-arrow')
        .forEach((arrow) => {
          const arrowEl = arrow as HTMLElement;
          arrowEl.classList.remove(
            'mat-sort-header-arrow-asc',
            'mat-sort-header-arrow-desc'
          );
          arrowEl.style.removeProperty('display');
          arrowEl.style.removeProperty('visibility');
          arrowEl.style.removeProperty('opacity');
          arrowEl.style.removeProperty('transform');
        });

      headerElement
        .querySelectorAll('.mat-sort-header-arrow-container')
        .forEach((container) => {
          const containerEl = container as HTMLElement;
          containerEl.style.removeProperty('display');
          containerEl.style.removeProperty('visibility');
          containerEl.style.removeProperty('opacity');
        });
    });
  }

  /** Apply sort arrow styling to exactly one column. */
  private applyActiveSortHeaderVisual(
    sortColumn: string,
    sortDirection: 'asc' | 'desc'
  ): void {
    this.clearAllSortHeaderVisuals();

    const sortHeaderElement = this.findSortHeaderElement(sortColumn);
    if (!sortHeaderElement) {
      return;
    }

    sortHeaderElement.classList.add('mat-sort-header-sorted');
    sortHeaderElement.setAttribute(
      'aria-sort',
      sortDirection === 'asc' ? 'ascending' : 'descending'
    );

    const arrowElement = sortHeaderElement.querySelector(
      '.mat-sort-header-arrow'
    ) as HTMLElement | null;
    if (arrowElement) {
      arrowElement.classList.add(`mat-sort-header-arrow-${sortDirection}`);
      arrowElement.style.display = 'block';
      arrowElement.style.visibility = 'visible';
      arrowElement.style.opacity = '1';
    }

    const arrowContainer = sortHeaderElement.querySelector(
      '.mat-sort-header-arrow-container'
    ) as HTMLElement | null;
    if (arrowContainer) {
      arrowContainer.style.display = 'block';
      arrowContainer.style.visibility = 'visible';
      arrowContainer.style.opacity = '1';
    }
  }

  /**
   * Manually updates sort header CSS classes to force arrow visibility
   */
  private updateSortHeaderClasses(
    sortColumn: string,
    sortDirection: string
  ): void {
    setTimeout(() => {
      this.applyActiveSortHeaderVisual(
        sortColumn,
        sortDirection as 'asc' | 'desc'
      );
      this.cdr.detectChanges();
    }, 50);
  }

  /**
   * Forces the sort arrow to be visible by manipulating the MatSort component state
   */
  private forceSortArrowVisibility(
    sortColumn: string,
    sortDirection: 'asc' | 'desc'
  ): void {
    if (!this.sort) {
      return;
    }

    this.sort.disableClear = true;
    this.sort.active = sortColumn;
    this.sort.direction = sortDirection;

    this.cdr.detectChanges();

    setTimeout(() => {
      this.applyActiveSortHeaderVisual(sortColumn, sortDirection);
      this.cdr.detectChanges();
    }, 100);
  }

  /**
   * Initializes the default sort properly after the view is ready
   */
  private initializeDefaultSort(): void {

    // Determine which column to sort by
    let sortColumn: string | undefined;
    let sortDirection: 'asc' | 'desc' = 'asc';

    // First check if we have explicit default sort settings
    if (this.settings.defaultSortColumn && this.settings.defaultSortDirection) {
      sortColumn = this.settings.defaultSortColumn;
      sortDirection = this.settings.defaultSortDirection;
    }
    // Otherwise, auto-select first sortable column
    else if (
      this.settings.sortable &&
      this.settings.columns &&
      this.settings.columns.length > 0
    ) {
      const firstSortableColumn = this.settings.columns.find(
        (col) => col.sortable !== false
      );
      if (firstSortableColumn) {
        sortColumn = firstSortableColumn.key;
        sortDirection = 'asc';

        // Update settings to reflect the auto-selected sort
        this.settings.defaultSortColumn = sortColumn;
        this.settings.defaultSortDirection = sortDirection;
      }
    }

    if (!sortColumn) {
      return;
    }

    // Set our internal state immediately - this will make getCurrentSortColumn() return the right value
    this.currentSortColumn = sortColumn;
    this.currentSortDirection = sortDirection;
    this.isCustomSortActive = true;

    // Force change detection so the template bindings update
    this.cdr.detectChanges();

    // If MatSort is available, also set its state
    if (this.sort) {
      this.sort.disableClear = true;
      this.sort.active = sortColumn;
      this.sort.direction = sortDirection;
    }

    // Additional change detection to ensure template updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 10);
  }

  /**
   * Ensures the sort arrow is visible using multiple approaches
   */
  private ensureSortArrowIsVisible(
    sortColumn: string,
    sortDirection: 'asc' | 'desc'
  ): void {
    if (this.sort) {
      this.sort.active = sortColumn;
      this.sort.direction = sortDirection;
      this.sort.disableClear = true;
    }

    this.applyActiveSortHeaderVisual(sortColumn, sortDirection);
    this.cdr.detectChanges();
  }

  /**
   * Ensures sort headers are properly initialized and visible
   */
  private ensureSortHeadersInitialized(): void {

    // Wait for the DOM to be ready
    setTimeout(() => {
      const sortHeaders = this.getSortHeaderElements();

      sortHeaders.forEach((header) => {
        const headerElement = header as HTMLElement;
        const columnKey =
          headerElement.getAttribute('data-sort-column') ||
          headerElement.getAttribute('mat-sort-header');

        if (columnKey) {
          if (!headerElement.querySelector('.mat-sort-header-arrow')) {
            const arrowElement = document.createElement('div');
            arrowElement.className = 'mat-sort-header-arrow';
            arrowElement.setAttribute('aria-hidden', 'true');
            headerElement.appendChild(arrowElement);
          }

          if (
            columnKey === this.currentSortColumn &&
            this.currentSortDirection
          ) {
            this.forceSortArrowVisibility(columnKey, this.currentSortDirection);
          }
        }
      });
    }, 300);
  }

  /**
   * Custom sort click handler to ensure proper DESC -> ASC -> DESC cycling
   */
  onCustomSortClick(columnKey: string, event: Event): void {

    if (!this.settings.sortable) {
      return;
    }

    // Check if this column is sortable
    const column = this.settings.columns.find((col) => col.key === columnKey);
    if (!column || column.sortable === false) {
      return;
    }

    // Prevent any default behavior
    event.stopPropagation();
    event.preventDefault();

    // Determine the next sort direction - only toggle between ASC and DESC
    let nextDirection: 'asc' | 'desc';

    if (this.currentSortColumn === columnKey) {
      // Same column - toggle direction between ASC and DESC only
      nextDirection = this.currentSortDirection === 'desc' ? 'asc' : 'desc';
    } else {
      // Different column - start with desc
      nextDirection = 'desc';
    }

    // Update our custom sort state FIRST
    this.currentSortColumn = columnKey;
    this.currentSortDirection = nextDirection;
    this.isCustomSortActive = true;

    // Update settings to reflect current sort
    this.settings.defaultSortColumn = columnKey;
    this.settings.defaultSortDirection = nextDirection;

    // CRITICAL FIX: Update the MatSort component to match our custom sort state
    if (this.sort) {
      this.sort.disableClear = true; // Prevent clearing sort state - always set this first
      this.sort.active = columnKey;
      this.sort.direction = nextDirection;
    } else {
    }

    // Reset to first page when sorting
    this.pageIndex = 0;

    // Update internal filters directly without calling onSortChange
    const otherFilters = this._internalFilters.filter(
      (f) => f.column !== 'sortColumn' && f.column !== 'sortDirection'
    );

    const sortFilters: TableFilter[] = [
      {
        column: 'sortColumn',
        value: columnKey,
        operator: 'equals',
        label: 'Sort Column',
        options: [],
      },
      {
        column: 'sortDirection',
        value: nextDirection,
        operator: 'equals',
        label: 'Sort Direction',
        options: [],
      },
    ];

    this._internalFilters = [...otherFilters, ...sortFilters];

    // Force change detection to update the UI immediately (show arrow)
    this.cdr.detectChanges();

    // Also force the sort arrow to be visible
    this.forceSortArrowVisibility(columnKey, nextDirection);

    // Log current state before loading data

    // Load data with the updated filters - backend will handle sorting
    this.loadDataWithFilters();
  }

  /** Columns shown in the mobile card layout (excludes action columns). */
  getMobileDataColumns(): TableColumn[] {
    let columns = this.settings.columns;

    if (!this.isModuleColumnVisible()) {
      columns = columns.filter((col) => !this.isModuleColumn(col));
    }

    return columns.filter(
      (col) =>
        col.type !== 'button' &&
        col.type !== 'icon-button' &&
        col.type !== 'menu'
    );
  }

  getMobilePrimaryColumn(): TableColumn | undefined {
    const columns = this.getMobileDataColumns();
    return (
      columns.find((col) => col.key === 'name') ??
      columns.find((col) => col.type === 'text' || col.type === 'component') ??
      columns[0]
    );
  }

  getMobileSecondaryColumns(): TableColumn[] {
    const primary = this.getMobilePrimaryColumn();
    if (!primary) {
      return [];
    }

    return this.getMobileDataColumns().filter((col) => col.key !== primary.key);
  }

  getMobileActionColumns(): TableColumn[] {
    return this.settings.columns.filter(
      (col) =>
        col.type === 'button' ||
        col.type === 'icon-button' ||
        col.type === 'menu'
    );
  }

  getVisibleMenuItems(column: TableColumn, row: any): TableMenuItemDef[] {
    if (!column.menuItems?.length) {
      return [];
    }
    return column.menuItems.filter((item) => !item.showIf || item.showIf(row));
  }

  shouldShowMobileField(column: TableColumn, row: any): boolean {
    if (column.showIf && !column.showIf(row)) {
      return false;
    }

    const value = this.getColumnValue(row, column);
    return (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== '' &&
      String(value).trim() !== '-'
    );
  }

  isMobileActionColumn(column: TableColumn): boolean {
    return (
      column.type === 'button' ||
      column.type === 'icon-button' ||
      column.type === 'menu'
    );
  }

  getMobilePrimarySubtitle(row: any): string | null {
    const primary = this.getMobilePrimaryColumn();
    if (!primary?.subtitle) {
      return null;
    }

    const value = primary.subtitle(row);
    return value ? String(value) : null;
  }

  trackMobileRow(index: number, row: any): string | number {
    if (row?.isNoDataRow) {
      return 'no-data';
    }

    return row?.id ?? row?.participant_id ?? row?.user_id ?? index;
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth < environment.mobileWidth;
  }

  onRowClick(row: any) {
    if (this.useCardLayout) {
      if (this.settings.onRowClick) {
        this.settings.onRowClick(row);
        return;
      }

      this.openRowDetailsModal(row);
      return;
    }

    if (this.settings.onRowClick) {
      this.settings.onRowClick(row);
    }
  }

  private openRowDetailsModal(row: any) {
    const buttonColumn = this.settings.columns.find(
      (col) => col.type === 'button'
    );
    const action =
      buttonColumn?.opensRowDetailsModal || !buttonColumn?.action
        ? undefined
        : buttonColumn.action;

    this.dialog.open(RowDetailsModalComponent, {
      data: {
        row,
        columns: this.settings.columns,
        extraFields: this.settings.rowDetailExtras,
        action: action ? (rowData: any) => action(rowData) : undefined,
      },
      width: '100%',
      maxWidth: '560px',
      panelClass: 'mobile-dialog',
    });
  }

  // Listen for SurveyJS initialization event
  @HostListener('surveyjs-init', ['$event'])
  onSurveyJSInit(event: Event): void {
    const e = event as CustomEvent;

    if (e.detail && e.detail.questionData) {
      this.isSurveyJSComponent = true;
      const questionData = e.detail.questionData;

      // Set component inputs from question data
      if (questionData.dataSource) {
        this.dynamicInput = {
          dataSource: { type: 'text', value: questionData.dataSource },
        };
      }

      if (questionData.enableSearch !== undefined) {
        this.dynamicInput = {
          enableSearch: {
            type: 'boolean',
            value: questionData.enableSearch === true,
          },
        };
      }

      if (questionData.multiSelect !== undefined) {
        this.dynamicInput = {
          multiSelect: {
            type: 'boolean',
            value: questionData.multiSelect === true,
          },
        };
      }

      if (questionData.simpleMultiSelect !== undefined) {
        this.dynamicInput = {
          simpleMultiSelect: {
            type: 'boolean',
            value: questionData.simpleMultiSelect === true,
          },
        };
      }

      if (questionData.selectionKeyField) {
        this.dynamicInput = {
          selectionKeyField: {
            type: 'text',
            value: questionData.selectionKeyField,
          },
        };
      }

      if (questionData.persistSelections !== undefined) {
        this.dynamicInput = {
          persistSelections: {
            type: 'boolean',
            value: questionData.persistSelections === true,
          },
        };
      }

      if (questionData.persistSearch !== undefined) {
        this.dynamicInput = {
          persistSearch: {
            type: 'boolean',
            value: questionData.persistSearch === true,
          },
        };
      }

      if (questionData.filters) {
        this.dynamicInput = {
          filters: { type: 'json', value: questionData.filters },
        };
      }

      // Create settings object
      const settings = {
        pageable: true,
        pageSize: questionData.pageSize || 5, // Default to 5 to match first option
        sortable: true,
        filterable: true,
        striped: questionData.striped !== false,
        dense: questionData.dense === true,
        rowHover: questionData.rowHover !== false,
        searchable: questionData.enableSearch === true,
        defaultSortColumn: questionData.defaultSortColumn || '',
        defaultSortDirection: questionData.defaultSortDirection || 'desc',
        multiSelect: questionData.multiSelect === true,
      };

      // Apply settings
      this.dynamicInput = {
        settings: { type: 'json', value: JSON.stringify(settings) },
      };

      // Initialize the component with the new settings
      this.ngOnInit();

      // Force reload of data source after SurveyJS context is detected
      setTimeout(() => {
        this.reloadDataSource();
      }, 100);

      // Force update paginator settings after initialization
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 200);

      // Also force update after data is loaded
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 500);
    }
  }

  // Check if this component is inside a SurveyJS question
  public checkForSurveyJSContext(): void {
    const element = this.elementRef.nativeElement;
    const hasSurveyJSAttribute = element.hasAttribute('surveyjs-component');

    if (hasSurveyJSAttribute && !this.isSurveyJSComponent) {
      this.isSurveyJSComponent = true;

      // Apply special styles for SurveyJS context
      this.applyFullWidthStyle();

      // Get the question data directly from element if available
      const questionData = (element as any).surveyJSQuestionData;
      if (questionData) {

        // Ensure dynamicInput is initialized
        if (!this.dynamicInput) {
          this.dynamicInput = {};
        }

        // Update inputs from question data
        if (questionData.dataSource) {
          this.dynamicInput = {
            dataSource: { type: 'text', value: questionData.dataSource },
          };
        }

        if (questionData.enableSearch !== undefined) {
          this.dynamicInput = {
            enableSearch: {
              type: 'boolean',
              value: questionData.enableSearch === true,
            },
          };
        }

        if (questionData.multiSelect !== undefined) {
          this.dynamicInput = {
            multiSelect: {
              type: 'boolean',
              value: questionData.multiSelect === true,
            },
          };

          // When used in SurveyJS, multiSelect should default to simple mode
          this.dynamicInput = {
            simpleMultiSelect: {
              type: 'boolean',
              value: questionData.multiSelect === true,
            },
          };
        }

        if (questionData.selectionKeyField) {
          this.dynamicInput = {
            selectionKeyField: {
              type: 'text',
              value: questionData.selectionKeyField,
            },
          };
        }

        if (questionData.persistSelections !== undefined) {
          this.dynamicInput = {
            persistSelections: {
              type: 'boolean',
              value: questionData.persistSelections === true,
            },
          };
        }

        if (questionData.persistSearch !== undefined) {
          this.dynamicInput = {
            persistSearch: {
              type: 'boolean',
              value: questionData.persistSearch === true,
            },
          };
        }

        if (questionData.filters) {
          this.dynamicInput = {
            filters: { type: 'json', value: questionData.filters },
          };
        }

        // Create settings object
        const settings = {
          pageable: true,
          pageSize: questionData.pageSize || 5, // Default to 5 to match first option
          sortable: true,
          filterable: true,
          striped: questionData.striped !== false,
          dense: questionData.dense === true,
          rowHover: questionData.rowHover !== false,
          searchable: questionData.enableSearch === true,
          defaultSortColumn: questionData.defaultSortColumn || '',
          defaultSortDirection: questionData.defaultSortDirection || 'desc',
          multiSelect: questionData.multiSelect === true,
        };

        // Apply settings
        this.dynamicInput = {
          settings: { type: 'json', value: JSON.stringify(settings) },
        };

        // Initialize the component with the new settings
        this.ngOnInit();

        // Force reload of data source after SurveyJS context is detected
        setTimeout(() => {
          this.reloadDataSource();
        }, 100);

        // Force update paginator settings after initialization
        setTimeout(() => {
          this.forceUpdatePaginatorSettings();
        }, 200);

        // Also force update after data is loaded
        setTimeout(() => {
          this.forceUpdatePaginatorSettings();
        }, 500);
      }

      // Notify the SurveyJS widget that we've initialized
      const initFunction = (window as any)
        .surveyJSInitDynamicTable as SurveyJSInitFunction;
      if (initFunction && typeof initFunction === 'function') {
        try {
          // This will attempt to use our component instance with the SurveyJS initialization function
          initFunction(
            this,
            questionData || {
              dataSource: element.getAttribute('data-source') || '',
            }
          );
        } catch (error) {
          console.error(
            '[DynamicTable] Error during SurveyJS initialization:',
            error
          );
        }
      }

      // Force update paginator settings for SurveyJS components
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 500);

      // Also force update after a longer delay to ensure everything is loaded
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 1000);

      // And one more time after data is fully loaded
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 1500);

      // Fix paginator selects for SurveyJS context
      setTimeout(() => {
        this.fixPaginatorSelects();
      }, 300);
    }
  }

  // Add a new method to apply full width styles
  private applyFullWidthStyle(): void {
    const element = this.elementRef.nativeElement;

    // Apply inline styles
    element.style.width = '100%';
    element.style.maxWidth = 'none';
    element.style.display = 'block';

    // Find the closest SurveyJS containers and remove width constraints
    let parent = element.parentElement;
    while (parent) {
      if (
        parent.classList.contains('sd-question') ||
        parent.classList.contains('sd-row') ||
        parent.classList.contains('sd-page') ||
        parent.classList.contains('sd-question__content')
      ) {
        parent.style.width = '100%';
        parent.style.maxWidth = 'none';
      }
      parent = parent.parentElement;
    }

    // Add a style element for table-specific rules
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      app-dynamic-table.survey-component {
        width: 100% !important;
        max-width: none !important;
        display: block !important;
      }
      
      app-dynamic-table.survey-component .mat-mdc-table {
        width: 100% !important;
      }
    `;

    // Add the style class to this element
    element.classList.add('survey-component');

    // Add the style element to the head
    document.head.appendChild(styleElement);
  }

  // Listen for SurveyJS refresh event
  @HostListener('surveyjs-refresh', ['$event'])
  onSurveyJSRefresh(event: Event): void {
    const e = event as CustomEvent;

    if (e.detail && e.detail.questionData) {
      this.isSurveyJSComponent = true;
      const questionData = e.detail.questionData;

      // Ensure dynamicInput is initialized
      if (!this.dynamicInput) {
        this.dynamicInput = {};
      }

      // Update component inputs from question data
      if (questionData.dataSource) {
        this.dynamicInput = {
          dataSource: { type: 'text', value: questionData.dataSource },
        };
      }

      if (questionData.enableSearch !== undefined) {
        this.dynamicInput = {
          enableSearch: {
            type: 'boolean',
            value: questionData.enableSearch === true,
          },
        };
      }

      if (questionData.multiSelect !== undefined) {
        this.dynamicInput = {
          multiSelect: {
            type: 'boolean',
            value: questionData.multiSelect === true,
          },
        };
      }

      if (questionData.simpleMultiSelect !== undefined) {
        this.dynamicInput = {
          simpleMultiSelect: {
            type: 'boolean',
            value: questionData.simpleMultiSelect === true,
          },
        };
      }

      if (questionData.selectionKeyField) {
        this.dynamicInput = {
          selectionKeyField: {
            type: 'text',
            value: questionData.selectionKeyField,
          },
        };
      }

      if (questionData.persistSelections !== undefined) {
        this.dynamicInput = {
          persistSelections: {
            type: 'boolean',
            value: questionData.persistSelections === true,
          },
        };
      }

      if (questionData.persistSearch !== undefined) {
        this.dynamicInput = {
          persistSearch: {
            type: 'boolean',
            value: questionData.persistSearch === true,
          },
        };
      }

      if (questionData.filters) {
        this.dynamicInput = {
          filters: { type: 'json', value: questionData.filters },
        };
      }

      // Reapply width fix each time we refresh to ensure it stays applied
      this.applyFullWidthStyle();

      // Force component to reload with new settings
      this.ngOnInit();

      // Force change detection
      this.cdr.detectChanges();

      // Force update paginator settings for SurveyJS components
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 500);

      // Also force update after a longer delay to ensure everything is loaded
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 1000);

      // And one more time after data is fully loaded
      setTimeout(() => {
        this.forceUpdatePaginatorSettings();
      }, 1500);
    }
  }

  /** Update the selection model from persisted selections */
  private updateSelectionFromPersisted(): void {
    if (!this.persistSelections || !this.selectionKeyField) {
      return;
    }

    // Get current data from the paginated data observable
    const currentData = this.paginatedData$.value || [];
    const newSelection: any[] = [];

    // Only process selectable items
    const selectableItems = currentData.filter(
      (item) =>
        !item.isNoDataRow &&
        item.participant_id !== 0 &&
        item.name !== 'Der er endnu ingen data'
    );

    selectableItems.forEach((item) => {
      const key = this.getRowIdentifier(item);
      if (this.persistentSelections.has(key)) {
        newSelection.push(item);
      }
    });

    // Clear current selection and select the restored items
    this.selection.clear();
    if (newSelection.length > 0) {
      this.selection.select(...newSelection);
    } else {
    }

    // Force change detection to update checkboxes visually
    this.cdr.detectChanges();

    // Emit the selection change to notify any listeners - but only emit persistent selections, not just current page
    if (this.persistSelections) {
      // For persistent selections, emit all persistently selected items, not just current page
      const allPersistentItems = Array.from(this.persistentSelections.values());
      this.rowSelectionChange.emit(allPersistentItems);

      // If we're using simple multi-select with a key field, emit special output
      if (this.simpleMultiSelect && this.selectionKeyField) {
        const selectedValues = allPersistentItems
          .map((row) => {
            return row[this.selectionKeyField];
          })
          .filter((val) => val !== undefined);

        this.simpleSelectionChange.emit(selectedValues);
      }
    } else {
      // Standard behavior
      this.emitSelectionChange();
    }
  }

  getButtonLabel(column: TableColumn, row: any): string {
    if (column.buttonTextFn) {
      return column.buttonTextFn(row);
    }
    return column.buttonText ?? '';
  }

  getButtonColor(column: TableColumn, row: any): string | undefined {
    if (column.buttonColorFn) {
      return column.buttonColorFn(row);
    }
    return column.buttonColor;
  }

  getButtonVariant(column: TableColumn, row: any): string {
    if (column.variantFn) {
      const dynamic = column.variantFn(row);
      if (dynamic) {
        return dynamic;
      }
    }
    return column.variant ?? '';
  }

  getButtonClasses(column: TableColumn, row: any): string {
    const cls = column.buttonClassFn?.(row);
    if (!cls) {
      return '';
    }
    return Array.isArray(cls) ? cls.join(' ') : cls;
  }

  // Method to properly get column values, handling getters and transforms
  getRowEventTypeIcon(element: any, column: TableColumn): string | null {
    const field = column.eventTypeIconFrom;
    if (!field || !element) return null;
    const raw = element[field];
    if (raw == null || String(raw).trim() === '') return null;
    return getEventTypeMaterialIcon(String(raw));
  }

  getRowEventTypeIconLabel(element: any, column: TableColumn): string {
    const field = column.eventTypeIconFrom;
    if (!field || !element) return '';
    return getEventTypeLabelDa(element[field]);
  }

  getColumnValue(element: any, column: TableColumn): string {
    if (element?.isNoDataRow) {
      const firstColumnKey = this.settings.columns[0]?.key;
      if (column.key === firstColumnKey) {
        return element.message || this.noDataMessage;
      }
      return '';
    }

    // Handle transforms first
    if (column.transform) {
      return column.transform(element[column.key], element);
    }

    // Handle format function
    if (column.format) {
      return column.format(element[column.key]);
    }

    // For getter properties like 'name', try to access the property directly first
    // This handles cases where element[column.key] might not work for getters
    let value;

    try {
      // First try direct property access (works for getters and regular properties)
      if (element && typeof element === 'object') {
        // Check if the property exists as a getter or regular property
        const descriptor =
          Object.getOwnPropertyDescriptor(element, column.key) ||
          Object.getOwnPropertyDescriptor(
            Object.getPrototypeOf(element),
            column.key
          );

        if (descriptor && descriptor.get) {
          // It's a getter, call it
          value = element[column.key];
        } else if (column.key in element) {
          // It's a regular property
          value = element[column.key];
        }
      }

      // If still undefined, try alternative approaches based on known patterns
      if (value === undefined || value === null) {
        // Special handling for known getter properties
        if (column.key === 'name') {
          if (
            element.first_name !== undefined ||
            element.last_name !== undefined
          ) {
            value = `${element.first_name || ''} ${
              element.last_name || ''
            }`.trim();
          } else if (
            element.firstName !== undefined ||
            element.lastName !== undefined
          ) {
            value = `${element.firstName || ''} ${
              element.lastName || ''
            }`.trim();
          }
        } else if (column.key === 'primaryLocation') {
          // Handle primaryLocation getter - match User.primaryLocation: prefer locations_name over roles.primaer
          // (rows are often JSON-cloned for pagination, which strips getters so primaer must not win over locations_name)
          if (element.locations && element.locations.length > 0) {
            if (element.locations[0].locations_name) {
              value = element.locations[0].locations_name;
            } else if (element.locations[0].name) {
              value = element.locations[0].name;
            }
          }
          if (value === undefined || value === null || value === '') {
            if (element.location_name) {
              value = element.location_name;
            } else if (element.locationName) {
              value = element.locationName;
            } else if (
              element.roles &&
              element.roles.primaer &&
              element.roles.primaer.length > 0
            ) {
              value = element.roles.primaer[0];
            } else if (element.location && element.location.name) {
              value = element.location.name;
            } else {
              value = '-';
            }
          }
        } else if (column.key === 'educationalPathsText') {
          // Handle educationalPathsText getter
          if (
            element.event_educational_paths &&
            Array.isArray(element.event_educational_paths)
          ) {
            value = element.event_educational_paths
              .map((ep: any) => ep.name || ep.title || ep)
              .join(', ');
          } else if (
            element.educational_paths &&
            Array.isArray(element.educational_paths)
          ) {
            value = element.educational_paths
              .map((ep: any) => ep.name || ep.title || ep)
              .join(', ');
          } else if (
            element.educationalPaths &&
            Array.isArray(element.educationalPaths)
          ) {
            value = element.educationalPaths
              .map((ep: any) => ep.name || ep.title || ep)
              .join(', ');
          } else {
            value = '-';
          }
        }
      }
    } catch (error) {
      console.warn(
        `[DynamicTable] Error accessing column ${column.key}:`,
        error
      );
      value = '';
    }

    return value !== undefined && value !== null ? String(value) : '';
  }

  onRemoveSelected() {
    if (!this.settings.onRemoveSelected) {
      return;
    }

    const selectedItems = this.selection.selected;
    if (selectedItems.length === 0) {
      return;
    }

    // Call the callback with selected items
    this.settings.onRemoveSelected(selectedItems);
  }

  hasSelectedItems(): boolean {
    return this.selection.selected.length > 0;
  }

  getRemoveButtonText(): string {
    const count = this.selection.selected.length;
    const baseText = this.settings.removeButtonText || 'Slet valgte';
    return count > 0 ? `${baseText} (${count})` : baseText;
  }

  /** Get the total number of selected items (including persistent selections) */
  getTotalSelectedCount(): number {
    if (this.persistSelections && this.selectionKeyField) {
      return this.persistentSelections.size;
    }
    return this.selection.selected.length;
  }

  /** Get the number of persistent selections for template access */
  getPersistentSelectionsCount(): number {
    return this.persistentSelections.size;
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /** Check if component is in SurveyJS context (for template access) */
  isInSurveyJSContext(): boolean {
    return this.isSurveyJSComponent;
  }

  /** Force update paginator settings to ensure pageSizeOptions are properly set */
  forceUpdatePaginatorSettings(): void {

    // Force change detection to update the paginator
    this.cdr.detectChanges();

    // Also try to find the paginator element and update it directly
    setTimeout(() => {
      const paginator = document.querySelector('mat-paginator');
      if (paginator) {
        const pageSizeOptionsAttr = paginator.getAttribute(
          'ng-reflect-page-size-options'
        );

        // Try to force update the paginator by setting the attribute directly
        if (
          this.settings.pageSizeOptions &&
          this.settings.pageSizeOptions.length > 0
        ) {
          const optionsString = JSON.stringify(this.settings.pageSizeOptions);

          // Force the paginator to update by triggering change detection
          this.cdr.detectChanges();

          // Also try to update the paginator's internal state
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 50);
        }
      }
    }, 100);
  }

  /** Fix paginator select dropdown issues */
  private fixPaginatorSelects(): void {

    // Use a MutationObserver to watch for paginator select panels
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check if this is a paginator select panel
            const paginatorSelects = element.querySelectorAll(
              '.mat-paginator-page-size-select-panel, .mat-mdc-paginator-page-size-select-panel'
            );
            paginatorSelects.forEach((select) => {
              const selectEl = select as HTMLElement;

              // Check if there are modals present
              const hasModals =
                document.querySelector('.modal-overlay-panel') !== null;

              // Only apply basic styling, let the modal service handle z-index
              selectEl.style.maxWidth = '200px';
              selectEl.style.minWidth = 'auto';
              selectEl.style.width = 'auto';
              selectEl.style.position = 'relative';

              // Fix the options
              const options = selectEl.querySelectorAll(
                '.mat-paginator-page-size-option, .mat-mdc-paginator-page-size-option'
              );
              options.forEach((option) => {
                const optionEl = option as HTMLElement;
                optionEl.style.whiteSpace = 'nowrap';
                optionEl.style.textOverflow = 'ellipsis';
                optionEl.style.overflow = 'hidden';
                optionEl.style.maxWidth = '100%';
              });
            });
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also fix any existing paginator selects
    setTimeout(() => {
      const existingSelects = document.querySelectorAll(
        '.mat-paginator-page-size-select-panel, .mat-mdc-paginator-page-size-select-panel'
      );

      existingSelects.forEach((select) => {
        const selectEl = select as HTMLElement;
        selectEl.style.maxWidth = '200px';
        selectEl.style.minWidth = 'auto';
        selectEl.style.width = 'auto';
        selectEl.style.position = 'relative';

        // Fix the options
        const options = selectEl.querySelectorAll(
          '.mat-paginator-page-size-option, .mat-mdc-paginator-page-size-option'
        );
        options.forEach((option) => {
          const optionEl = option as HTMLElement;
          optionEl.style.whiteSpace = 'nowrap';
          optionEl.style.textOverflow = 'ellipsis';
          optionEl.style.overflow = 'hidden';
          optionEl.style.maxWidth = '100%';
        });
      });
    }, 100);
  }

  /** Check if batch editing is available */
  isBatchEditingAvailable(): boolean {
    const isAvailable =
      this.settings.multiSelect === true &&
      (this.selection.selected.length > 0 ||
        (this.persistSelections && this.getPersistentSelectionsCount() > 0)) &&
      this.hasEditableColumns();

    return isAvailable;
  }

  /** Check if there are any editable columns */
  hasEditableColumns(): boolean {
    return this.settings.columns.some((col) => col.editable === true);
  }

  /** Get count of editable columns */
  getEditableColumnsCount(): number {
    return this.settings.columns.filter((col) => col.editable === true).length;
  }

  /** Print the table content */
  printTable(): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert(
        'Pop-up blocker is preventing the print window from opening. Please allow pop-ups for this site.'
      );
      return;
    }

    // Get current data to print
    const dataToPrint = this.paginatedData$.value || [];

    // Build HTML for printing
    const printHtml = this.generatePrintHtml(dataToPrint);

    printWindow.document.write(printHtml);
    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  }

  /** Generate HTML content for printing */
  private generatePrintHtml(data: any[]): string {
    const title = document.title || 'Table Data';
    const printDate = new Date().toLocaleDateString('da-DK');

    // Filter visible columns (exclude select column and buttons)
    const printableColumns = this.settings.columns.filter(
      (col) =>
        col.type !== 'button' &&
        col.key !== 'select' &&
        !col.key.startsWith('_')
    );

    let tableRows = '';

    // Generate header row
    const headerCells = printableColumns
      .map(
        (col) =>
          `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f5f5f5; font-weight: bold;">${col.header}</th>`
      )
      .join('');

    tableRows += `<tr>${headerCells}</tr>`;

    // Generate data rows
    data.forEach((row) => {
      if (row.isNoDataRow || row.name === 'Der er endnu ingen data') return;

      const dataCells = printableColumns
        .map((col) => {
          let cellValue = row[col.key] || '';

          // Apply column transform if available
          if (col.transform && typeof col.transform === 'function') {
            cellValue = col.transform(cellValue, row);
          } else if (col.format && typeof col.format === 'function') {
            cellValue = col.format(cellValue);
          }

          // Handle boolean values
          if (typeof cellValue === 'boolean') {
            cellValue = cellValue ? 'Ja' : 'Nej';
          }

          // Handle dates
          if (col.type === 'date' && cellValue) {
            try {
              const date = new Date(cellValue);
              cellValue = date.toLocaleDateString('da-DK');
            } catch (e) {
              // Keep original value if date parsing fails
            }
          }

          return `<td style="border: 1px solid #ddd; padding: 8px;">${cellValue}</td>`;
        })
        .join('');

      tableRows += `<tr>${dataCells}</tr>`;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Print - ${title}</title>
        <style>
          @media print {
            @page { margin: 1cm; }
            body { font-family: Arial, sans-serif; font-size: 12px; }
          }
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; margin-bottom: 10px; }
          .print-info { margin-bottom: 20px; color: #666; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { text-align: left; }
          .no-data { text-align: center; font-style: italic; color: #666; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="print-info">
          Udskrevet: ${printDate}<br>
          Antal rækker: ${data.length}
        </div>
        <table>
          ${tableRows}
        </table>
        ${data.length === 0 ? '<p class="no-data">Ingen data at vise</p>' : ''}
      </body>
      </html>
    `;
  }
}
